import React, { useEffect, useState, useCallback, useRef } from 'react';
import useChromeCollapse from '../hooks/useChromeCollapse';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useToast } from '../context/ToastContext';
import api from '../api/api';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, DocumentTextIcon, TableCellsIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, ShoppingBagIcon, Bars3Icon, QrCodeIcon, ShoppingCartIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../config/currency';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { useTheme } from '../context/ThemeContext';
import { ORDER_EVENTS, NOTIFICATION_EVENTS } from '../constants/socketEvents';
// import MobileDropdown from '../components/MobileDropdown';
import LoadingScreen from '../components/LoadingScreen';
import InvoiceModal from '../components/InvoiceModal';
import { isNotificationSupported, getNotificationPermission, requestNotificationPermission } from '../utils/notificationUtils';
import DateFilterDropdown from '../components/DateFilterDropdown';
import SortDropdown from '../components/SortDropdown';
import UnifiedFilterDropdown from '../components/UnifiedFilterDropdown';
import FilterSidebar from '../components/FilterSidebar';
import { mapStatusToBackend } from '../utils/statusMapping';
import SearchModal from '../components/SearchModal';
import SpiceLevelBadge from '../components/SpiceLevelBadge';
import { DisplayContext, formatOrderId, getOrderTypeDisplay, formatRelativeTime } from '../utils/orderFormatter';
import '../styles/comprehensive-responsive-system.css';
import '../styles/vendor-orders-full-width.css';
import OrderDetailModal from '../components/OrderDetailModal';
import ImprovedVendorOrderFormModal from '../components/ImprovedVendorOrderFormModal';
import type { HeldOrder } from '../types/heldOrder';
import { getHeldOrders, deleteHeldOrder as deleteHeldOrderFromStore } from '../utils/heldOrderStore';
import { PauseIcon } from '@heroicons/react/24/outline';
import WindowsScrollArrows from '../components/WindowsScrollArrows';
import MenuItemImage from '../components/MenuItemImage';
import CompactOrderCard from '../components/CompactOrderCard';
import { groupOrdersByTable, getGroupDisplayName } from '../utils/orderGrouping';
import { CURRENCY } from '../config/currency';
import { getPaymentMethods } from '../config/paymentMethods';

const LazyHeldOrdersPicker = React.lazy(() => import('../components/HeldOrdersPicker'));

/* Cooking-themed inline spinner — randomly picks an SVG icon each render */
const COOKING_ICONS = [
  // Pan/skillet
  (color: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="7" /><line x1="19" y1="12" x2="23" y2="12" /><path d="M9 9c0 0 1.5-1 3-1s3 1 3 1" /><path d="M8 15s1.5 1 4 1 4-1 4-1" />
    </svg>
  ),
  // Chef hat
  (color: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 13c-1.66 0-3-1.34-3-3 0-1.31.84-2.42 2-2.83C5.04 5.36 6.86 4 9 4c1.44 0 2.73.62 3.63 1.6A4.98 4.98 0 0 1 17 4c2.21 0 4 1.79 4 4 0 1.48-.81 2.77-2 3.46V18H5v-5z" />
      <line x1="5" y1="18" x2="19" y2="18" />
    </svg>
  ),
  // Flame/fire
  (color: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z" /><path d="M12 18a2 2 0 0 1-2-2c0-2 2-3 2-5 0 2 2 3 2 5a2 2 0 0 1-2 2z" />
    </svg>
  ),
  // Utensils (fork+knife)
  (color: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2V2" /><line x1="6" y1="2" x2="6" y2="22" /><path d="M16 2c0 4 3 6 3 10a3 3 0 0 1-6 0c0-4 3-6 3-10z" /><line x1="16" y1="16" x2="16" y2="22" />
    </svg>
  ),
  // Steam/pot
  (color: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="9" rx="2" /><line x1="2" y1="11" x2="22" y2="11" /><path d="M8 2c0 2-2 3-2 5" /><path d="M12 2c0 2-2 3-2 5" /><path d="M16 2c0 2-2 3-2 5" />
    </svg>
  ),
];

const CookingSpinner: React.FC<{ theme: any }> = ({ theme }) => {
  const [iconIdx] = React.useState(() => Math.floor(Math.random() * COOKING_ICONS.length));
  const color = theme.gray?.[400] || '#999';
  return (
    <div style={{ animation: 'spin 1.5s linear infinite', display: 'flex', alignItems: 'center' }}>
      {COOKING_ICONS[iconIdx](color)}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

interface OrderItem {
  id: number;
  // Support both old and new structures
  menuItem?: {
    id: number;
    name: string;
    price: number;
    description?: string;
  };
  itemName?: string; // New DTO structure
  quantity: number;
  price: number;
  comment?: string;
  toppings?: { id: number; name: string }[] | string[]; // New DTO returns string array
  portionName?: string; // e.g., "LARGE", "HALF", "FULL"
  requestedSpiceLevel?: string; // e.g., "MILD", "MEDIUM", "HOT", "EXTRA_HOT"
  spiceLevel?: string; // fallback field
}

interface Order {
  id: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  orderNumber?: string;
  user?: { id: number; username: string; email?: string } | null; // Optional for new DTO
  vendor?: { id: number; username: string }; // Optional for new DTO
  qrCode?: { id: number; code: string; location?: string } | string | null; // Can be object or string (new DTO returns string)
  items: OrderItem[];
  total?: number; // Calculate if not available
  orderType?: string; // TAKEAWAY, DELIVERY, COUNTER_SERVICE, TABLE_SERVICE
  deliveryType?: string;
  // Add position if it's part of the Order type from backend for pending orders, otherwise calculated client-side
  position?: number;
  // Timestamp fields for time tracking
  acceptedAt?: string;
  preparingAt?: string;
  readyAt?: string;
  completedAt?: string;
  // Guest order fields (old structure)
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  isGuestOrder?: boolean;
  // New DTO fields
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  tableName?: string;
  floorName?: string;
  sectionName?: string;
  customOrderId?: string;
  // Cancellation fields
  cancellationReason?: string;
  cancelledAt?: string;
  // Order-level notes
  orderNotes?: string;
  // Session (OrderLine) payment info
  orderLineId?: number;
  sessionTotalAmount?: number;
  sessionPaidAmount?: number;
  sessionRemainingAmount?: number;
  isSessionFullyPaid?: boolean;
  orderLineStatus?: string;
  isLastIncompleteOrder?: boolean;
  hasSessionDiscount?: boolean;
  // Discount information
  discountType?: string;
  discountValue?: number;
  discountAmount?: number;
  discountReason?: string;
  finalTotal?: number; // Total after discount
  // Staff order info (who placed the order)
  isStaffOrder?: boolean;
  placedByName?: string;
}

// Payment status check response from API
interface PaymentStatusResponse {
  needsPaymentConfirmation: boolean;
  sessionTotalAmount?: number;
  sessionPaidAmount?: number;
  sessionRemainingAmount?: number;
  isLastIncompleteOrder?: boolean;
  /** Discount on the session, if any — shown in the Confirm Payment popup */
  sessionDiscountAmount?: number;
}

interface OrderAnalytics {
  orderToAcceptedMinutes?: number;
  orderToAcceptedTime?: string;
  acceptedToPreparingMinutes?: number;
  acceptedToPreparingTime?: string;
  preparingToReadyMinutes?: number;
  preparingToReadyTime?: string;
  readyToCompletedMinutes?: number;
  readyToCompletedTime?: string;
  totalMinutes?: number;
  totalTime?: string;
  timestamps: {
    createdAt?: string;
    acceptedAt?: string;
    preparingAt?: string;
    readyAt?: string;
    completedAt?: string;
  };
}

interface KOTItem {
  id: number;
  itemName: string;
  quantity: number;
  specialInstructions?: string;
  status: string;
}

interface KOT {
  id: number;
  kotNumber: string;
  orderId: number;
  kitchenId: number;
  kitchenName: string;
  status: string;
  priority: string;
  tableNumber?: string;
  customerName?: string;
  specialInstructions?: string;
  createdAt: string;
  printedAt?: string;
  acknowledgedAt?: string;
  startedAt?: string;
  readyAt?: string;
  servedAt?: string;
  printCount: number;
  items: KOTItem[];
}

const VendorOrders: React.FC = () => {
  const { user, logout, isSteward } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { isConnected: connected, subscribeToVendor, unsubscribeFromVendor, on, off } = useSocket();
  const [hasPermission, setHasPermission] = useState(getNotificationPermission() === 'granted');
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setHasPermission(result === 'granted');
    return result === 'granted';
  }, []);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const hasLoadedOnce = useRef(false); // Track if initial load is done
  const [error, setError] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  // New: status filter via chips; null => no filter
  const chromeScrollRef = React.useRef<HTMLDivElement>(null);
  useChromeCollapse(React.useCallback(() => chromeScrollRef.current, []));
  const [statusFilter, setStatusFilter] = useState<string | null>('ACTIVE'); // Default to active orders (excluding COMPLETED/CANCELLED)
  // Date filter state
  const [dateFilter, setDateFilter] = useState<string>('today');
  // Order type filter state
  const [orderTypeFilters, setOrderTypeFilters] = useState<string[]>([]);
  // Filter sidebar state (desktop only) - open by default
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState<boolean>(true);
  // Search modal state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  // Takeaway order modal state
  const [isTakeawayModalOpen, setIsTakeawayModalOpen] = useState<boolean>(false);

  // Held orders state
  const [heldOrders, setHeldOrders] = useState<HeldOrder[]>([]);
  const [showHeldOrdersPicker, setShowHeldOrdersPicker] = useState(false);
  const [resumeHeldOrder, setResumeHeldOrder] = useState<HeldOrder | null>(null);

  const refreshHeldOrders = useCallback(() => {
    const vendorId = (user as any)?.vendorId || user?.id;
    if (vendorId) {
      // Only show takeaway held orders in VendorOrders page
      setHeldOrders(getHeldOrders(vendorId).filter(o => o.orderType === 'takeaway'));
    }
  }, [user]);

  useEffect(() => {
    refreshHeldOrders();
  }, [refreshHeldOrders]);

  // Notification banner dismissed state
  const [isNotificationBannerDismissed, setIsNotificationBannerDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('vendorNotificationBannerDismissed') === 'true';
    } catch {
      return false;
    }
  });

  // Cancellation modal state
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  // Table grouping state - enabled by default for modern dashboard experience
  const [isGroupedView, setIsGroupedView] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('vendorOrdersGroupedView');
      return saved !== null ? saved === 'true' : true; // Default to true
    } catch {
      return true;
    }
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Kanban table sub-groups: which are expanded (default: expanded)
  const [collapsedTableGroups, setCollapsedTableGroups] = useState<Set<string>>(new Set());
  // Highlighted table name (cross-column spotlight)
  const [highlightedTable, setHighlightedTable] = useState<string | null>(null);
  // Track which groups should show all orders (bypass status filter)
  const [showAllOrdersGroups, setShowAllOrdersGroups] = useState<Set<string>>(new Set());
  // All orders for grouping (without status filter) - used to show full status summary in group headers
  const [allOrdersForGrouping, setAllOrdersForGrouping] = useState<Order[]>([]);

  // Auto-open sidebar when filters are applied on larger screens
  useEffect(() => {
    if (orderTypeFilters.length > 0 && window.innerWidth >= 1280) {
      setIsFilterSidebarOpen(true);
    }
  }, [orderTypeFilters]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false); // True while any fetch is in progress
  // Loading indicator specifically for switching status chips
  const [switchingStatus, setSwitchingStatus] = useState<string | 'ALL' | null>(null);
  const switchingStartedAtRef = useRef<number | null>(null);
  const MIN_SWITCH_SPINNER_MS = 0; // no chip spinner; rely on page-level loader
  // Page-level loader minimum display duration
  const pageLoadingStartedAtRef = useRef<number | null>(null);
  const MIN_PAGE_LOADER_MS = 1000; // keep "Loading vendor orders..." visible a bit longer

  // Debounce mechanism to prevent multiple rapid clicks
  const [updatingOrders, setUpdatingOrders] = useState<Set<number>>(new Set());

  // Helper: when user clicks a status chip, briefly show a spinner on that chip
  const handleStatusChipClick = useCallback((next: string | null) => {
    // If the filter is already the same, toggle back to ACTIVE (except for ACTIVE itself which goes to null)
    const nextFilter = (statusFilter === next) ? (next === 'ACTIVE' ? null : 'ACTIVE') : next;
    // Set which chip is switching (use 'ALL' for null)
    setSwitchingStatus(null);
    switchingStartedAtRef.current = null;
    // Reset pagination and apply filter; fetchVendorOrders effect will clear switchingStatus
    setCurrentPage(0);
    setStatusFilter(nextFilter);
  }, [statusFilter]);


  // Statistics state - total counts across all orders (not just current page)
  const [statistics, setStatistics] = useState<{
    TOTAL: number;
    ACTIVE: number;
    PENDING: number;
    ACCEPTED: number;
    PREPARING: number;
    READY: number;
    SERVED: number;
    COMPLETED: number;
    CANCELLED: number;
  }>({
    TOTAL: 0,
    ACTIVE: 0,
    PENDING: 0,
    ACCEPTED: 0,
    PREPARING: 0,
    READY: 0,
    SERVED: 0,
    COMPLETED: 0,
    CANCELLED: 0
  });

  // Track if initial filter has been set based on statistics
  const initialFilterSetRef = useRef(false);

  // Initial filter is now ACTIVE by default - no need to override
  useEffect(() => {
    if (!initialFilterSetRef.current && statistics.TOTAL > 0) {
      initialFilterSetRef.current = true;
      // Keep ACTIVE as default, no need to change
    }
  }, [statistics]);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('queuePosition');
  const [sortDirection, setSortDirection] = useState<string>('asc');

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderAnalytics, setOrderAnalytics] = useState<OrderAnalytics | null>(null);
  const [orderKOTs, setOrderKOTs] = useState<KOT[]>([]);
  const [expandedKOTs, setExpandedKOTs] = useState<Set<number>>(new Set());
  const [updatingKOTs, setUpdatingKOTs] = useState<Set<number>>(new Set());
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  // Quick status change popup state
  const [markAsOpenForOrderId, setMarkAsOpenForOrderId] = useState<number | null>(null);
  const markAsMenuRef = useRef<HTMLDivElement | null>(null);
  const markAsTriggerRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const [markAsPlacement, setMarkAsPlacement] = useState<'above' | 'below'>('below');
  const [markAsAlignRight, setMarkAsAlignRight] = useState<boolean>(false);
  const [markAsMaxHeight, setMarkAsMaxHeight] = useState<number>(256);

  // Hide header and navigation when modal is open
  useEffect(() => {
    if (selectedOrder) {
      // Add classes to hide header and bottom navigation
      document.body.classList.add('modal-open');
      // Hide the header
      const header = document.querySelector('.app-header, header');
      if (header) {
        (header as HTMLElement).style.display = 'none';
      }
      // Hide the bottom navigation
      const bottomNav = document.querySelector('.vendor-bottom-nav, .fixed.bottom-0');
      if (bottomNav) {
        (bottomNav as HTMLElement).style.display = 'none';
      }
    } else {
      // Show header and bottom navigation when modal is closed
      document.body.classList.remove('modal-open');
      const header = document.querySelector('.app-header, header');
      if (header) {
        (header as HTMLElement).style.display = '';
      }
      const bottomNav = document.querySelector('.vendor-bottom-nav, .fixed.bottom-0');
      if (bottomNav) {
        (bottomNav as HTMLElement).style.display = '';
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      const header = document.querySelector('.app-header, header');
      if (header) {
        (header as HTMLElement).style.display = '';
      }
      const bottomNav = document.querySelector('.vendor-bottom-nav, .fixed.bottom-0');
      if (bottomNav) {
        (bottomNav as HTMLElement).style.display = '';
      }
    };
  }, [selectedOrder]);

  // Invoice Modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrderId, setInvoiceOrderId] = useState<number | null>(null);
  const [invoiceIncludeCompleted, setInvoiceIncludeCompleted] = useState(false);

  // Payment confirmation popup state (for inline status changes)
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<PaymentStatusResponse | null>(null);
  const [pendingPaymentOrderId, setPendingPaymentOrderId] = useState<number | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');

  // Discount state
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountOrderLineId, setDiscountOrderLineId] = useState<number | null>(null);
  const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FIXED' | 'COUPON'>('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [currentDiscount, setCurrentDiscount] = useState<{type: string; value: number; amount: number; reason: string} | null>(null);
  // What the promotion engine says applies to this bill. Same server call the
  // floor-plan screen makes, so the two can never quote different offers.
  const [billOffers, setBillOffers] = useState<{available: boolean; discountAmount: number; names: string[]} | null>(null);
  const [applyingOffer, setApplyingOffer] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  // Inline (persistent) money-action errors — enterprise-ui item 6
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  /** Last failed status-change (served/paid/…) per order, shown inline next to the row */
  const [orderActionErrors, setOrderActionErrors] = useState<Record<number, string>>({});

  const clearOrderActionError = (orderId: number) => {
    setOrderActionErrors(prev => {
      if (!(orderId in prev)) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };

  // Payment method options
  // Country-aware (UPI/Wallet are IN-only rails) — see config/paymentMethods
  const PAYMENT_METHODS = getPaymentMethods();



  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';

  // Item keyword styles for modern theme - maps item names to emojis and colors
  // Soft/subtle color palette for modern theme
  const itemKeywordStyles: Record<string, { emoji: string; color: string; gradient: string }> = {
    // Beverages - soft tones
    'tea': { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    'coffee': { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'juice': { emoji: '🧃', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'lassi': { emoji: '🥛', color: '#9B8AB8', gradient: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)' },
    'shake': { emoji: '🥤', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    'smoothie': { emoji: '🥤', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)' },
    'water': { emoji: '💧', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
    'soda': { emoji: '🥤', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'cola': { emoji: '🥤', color: '#8B6B61', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'lemonade': { emoji: '🍋', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Indian Food - soft earthy tones
    'biryani': { emoji: '🍚', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'curry': { emoji: '🍛', color: '#B07858', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'dal': { emoji: '🥘', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    'paneer': { emoji: '🧀', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'naan': { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'roti': { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'dosa': { emoji: '🥞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'idli': { emoji: '⚪', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
    'samosa': { emoji: '🥟', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    'pakora': { emoji: '🍤', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'chaat': { emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    'tandoori': { emoji: '🍗', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'tikka': { emoji: '🍢', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'masala': { emoji: '🌶️', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'korma': { emoji: '🍛', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    // Chinese - soft tones
    'noodle': { emoji: '🍜', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'fried rice': { emoji: '🍚', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    'manchurian': { emoji: '🥘', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'chowmein': { emoji: '🍝', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'momos': { emoji: '🥟', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    'spring roll': { emoji: '🌯', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    // Western - soft tones
    'pizza': { emoji: '🍕', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'burger': { emoji: '🍔', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'sandwich': { emoji: '🥪', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE7 0%, #ECFCCB 100%)' },
    'pasta': { emoji: '🍝', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'fries': { emoji: '🍟', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    'wrap': { emoji: '🌯', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    // Desserts - soft pink/purple
    'ice cream': { emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    'cake': { emoji: '🍰', color: '#A87898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    'gulab jamun': { emoji: '🟤', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'kheer': { emoji: '🥛', color: '#9B9B9B', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
    'halwa': { emoji: '🟠', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'jalebi': { emoji: '🟡', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Snacks & Appetizers
    'chips': { emoji: '🥔', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    'popcorn': { emoji: '🍿', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'salad': { emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    'soup': { emoji: '🥣', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    // Non-veg - soft tones
    'chicken': { emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    'mutton': { emoji: '🍖', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    'fish': { emoji: '🐟', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
    'prawn': { emoji: '🦐', color: '#B88888', gradient: 'linear-gradient(135deg, #FEF7F7 0%, #FDE8E8 100%)' },
    'egg': { emoji: '🥚', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Breads
    'paratha': { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'kulcha': { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    'puri': { emoji: '🟤', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    // Rice
    'rice': { emoji: '🍚', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
    'pulao': { emoji: '🍚', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Default
    'default': { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
  };

  // Get item style based on item name
  const getItemStyle = (itemName: string) => {
    if (!isModernTheme) return null;
    const lower = itemName.toLowerCase();
    for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
      if (lower.includes(keyword)) return style;
    }
    return itemKeywordStyles['default'];
  };

  // Theme is now user-selectable - removed forced theme override
  // Users can choose modernDashboard or vendorOrders via theme selector

  // Valid status transitions – allow forward jumps and cancel
  const getValidNextStatuses = useCallback((currentStatus: string): string[] => {
    const statusTransitions: { [key: string]: string[] } = {
      PENDING: ['ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
      ACCEPTED: ['PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
      PREPARING: ['READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
      READY: ['SERVED', 'COMPLETED', 'CANCELLED'],
      SERVED: ['COMPLETED'],
      COMPLETED: [],
      CANCELLED: [],
    };
    return statusTransitions[currentStatus] || [];
  }, []);

  const handleQuickStatusUpdate = async (orderId: number, newStatus: string) => {
    // Prevent multiple rapid clicks on the same order
    if (updatingOrders.has(orderId)) {
      console.log('⏳ Order', orderId, 'is already being updated, ignoring duplicate request');
      return;
    }

    // Close dropdown immediately to prevent multiple clicks
    setMarkAsOpenForOrderId(null);

    // For CANCELLED status, show modal to get cancellation reason
    if (newStatus === 'CANCELLED') {
      setCancelOrderId(orderId);
      setCancellationReason('');
      setCancelError(null);
      setIsCancelModalOpen(true);
      return;
    }

    // For COMPLETED status on table orders, check if payment confirmation is needed
    if (newStatus === 'COMPLETED') {
      const currentOrder = orders.find(o => o.id === orderId);
      // Only check for table orders (those with qrCode/tableName)
      if (currentOrder?.qrCode || currentOrder?.tableName) {
        try {
          const response = await api.get<PaymentStatusResponse>(`/orders/${orderId}/check-payment-status`);
          const data = response.data;

          if (data.needsPaymentConfirmation) {
            // Show payment confirmation popup
            setPaymentInfo(data);
            setPendingPaymentOrderId(orderId);
            setPaymentError(null);
            setShowPaymentConfirmation(true);
            return; // Don't proceed with status update yet
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          // On error, continue with normal status update
        }
      }
    }

    // Mark this order as being updated
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    clearOrderActionError(orderId); // retry clears the previous inline error

    console.log('🚀 Quick status update:', { orderId, newStatus, timestamp: new Date().toISOString() });

    // Find the order BEFORE the callbacks so we can use it for the toast
    const currentOrder = orders.find(o => o.id === orderId);

    try {
      // Map frontend status to backend status (ACKNOWLEDGED → ACCEPTED)
      const backendStatus = mapStatusToBackend(newStatus);
      console.log('📡 Making API call to:', `/orders/${orderId}/status`, { frontend: newStatus, backend: backendStatus });
      const response = await api.put<Order>(`/orders/${orderId}/status`, { status: backendStatus });
      console.log('✅ API response received:', response.data);
      console.log('📊 Order status in response:', response.data.status);

      // Update local state immediately with a more robust approach
      console.log('🔄 Updating local state...');

      // Force update the orders list immediately
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order =>
          order.id === orderId
            ? { ...order, status: newStatus, updatedAt: new Date().toISOString() }
            : order
        );
        console.log('🔄 Orders list updated, new status for order', orderId, ':', newStatus);
        return updatedOrders;
      });

      // Update statistics immediately
      setStatistics(prevStats => {
        const newStats = { ...prevStats };

        // Use currentOrder found above for previous status
        const previousStatus = currentOrder?.status;

        if (previousStatus && previousStatus !== newStatus) {
          // Decrease count for previous status
          if (previousStatus in newStats) {
            newStats[previousStatus as keyof typeof newStats] = Math.max(0, (newStats[previousStatus as keyof typeof newStats] as number) - 1);
          }

          // Increase count for new status
          if (newStatus in newStats) {
            newStats[newStatus as keyof typeof newStats] = (newStats[newStatus as keyof typeof newStats] as number) + 1;
          }
        }

        console.log('📊 Statistics updated immediately:', newStats);
        return newStats;
      });

      // Show success message immediately - use customOrderId from the order we found
      const displayOrderId = currentOrder?.customOrderId || currentOrder?.orderNumber;
      toast.success(`Order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} status updated to ${newStatus.replace('_', ' ')}!`);

      // Background refresh to ensure consistency (non-blocking)
      setTimeout(async () => {
        try {
          console.log('🔄 Background refresh for consistency...');
          await Promise.all([
            fetchOrderStatistics(),
            fetchVendorOrders(0, false)
          ]);
        } catch (error) {
          console.error('Background refresh failed:', error);
        }
      }, 1000);

    } catch (err: unknown) {
      console.error('❌ Failed to update order status (quick):', err);
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Failed to update order status.';
      console.error('❌ Error message:', errorMessage);
      toast.error(errorMessage);
      // Persist the error inline next to the order row (cleared on retry/success)
      setOrderActionErrors(prev => ({ ...prev, [orderId]: errorMessage }));
    } finally {
      // Remove this order from updating set
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Handle opening discount dialog from payment popup
  const handleOpenDiscountFromPayment = async (orderLineId: number) => {
    setDiscountOrderLineId(orderLineId);
    setDiscountType('PERCENTAGE');
    setDiscountValue('');
    setDiscountReason('');
    setDiscountError(null);
    setCouponCode('');
    setCurrentDiscount(null);

    // Before the bill is shown: put a live offer on, if the vendor has asked for
    // automatic offers. Idempotent, silent, and never a reason to stop the
    // dialog opening. Same call the floor-plan screen makes, so both screens
    // behave identically.
    try {
      await api.post(`/api/order-lines/${orderLineId}/offers/ensure`, {});
    } catch { /* best effort — the dialog must still open */ }

    // Fetch existing discount if any
    try {
      const discountResponse = await api.get(`/api/order-lines/${orderLineId}/discount`);
      if (discountResponse.data && discountResponse.data.hasDiscount) {
        setCurrentDiscount({
          type: discountResponse.data.discountType,
          value: discountResponse.data.discountValue,
          amount: discountResponse.data.discountAmount,
          reason: discountResponse.data.discountReason
        });
        setDiscountType(discountResponse.data.discountType);
        setDiscountValue(discountResponse.data.discountValue?.toString() || '');
        setDiscountReason(discountResponse.data.discountReason || '');
        if (discountResponse.data.couponCode) {
          setCouponCode(discountResponse.data.couponCode);
        }
      }
    } catch (error) {
      console.log('No existing discount or error fetching:', error);
    }

    // What is on offer for this bill. Read-only, and a failure must not stop
    // the dialog opening — staff can still key a discount in by hand.
    setBillOffers(null);
    try {
      const offersResponse = await api.get(`/api/order-lines/${orderLineId}/offers`);
      setBillOffers({
        available: !!offersResponse.data?.available,
        discountAmount: Number(offersResponse.data?.discountAmount || 0),
        names: (offersResponse.data?.offers || []).map((o: { name: string }) => o.name),
      });
    } catch {
      setBillOffers(null);
    }

    setShowDiscountDialog(true);
  };

  /**
   * Put the offer the server found onto this bill.
   *
   * Sends no figure — what applies is worked out again server-side, because the
   * happy hour may have ended since this dialog opened.
   */
  const handleApplyOffer = async () => {
    if (!discountOrderLineId) return;
    setApplyingOffer(true);
    try {
      const { data } = await api.post(`/api/order-lines/${discountOrderLineId}/offers/apply`, {});
      toast.success(`${(data.offers || []).join(' + ')} applied`);
      setShowDiscountDialog(false);
      await fetchOrders();
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Could not apply the offer');
    } finally {
      setApplyingOffer(false);
    }
  };

  // Handle applying discount
  const handleApplyDiscount = async () => {
    if (!discountOrderLineId) {
      toast.error('Order line not found');
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    if (!discountReason.trim()) {
      toast.error('Discount reason is mandatory');
      return;
    }

    if (discountType === 'COUPON' && !couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100) {
      toast.error('Percentage cannot exceed 100%');
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError(null); // retry clears the previous inline error
    try {
      const response = await api.post(`/api/order-lines/${discountOrderLineId}/discount`, {
        discountType,
        discountValue: parseFloat(discountValue),
        reason: discountReason.trim(),
        couponCode: discountType === 'COUPON' ? couponCode.trim() : undefined
      });

      if (response.data) {
        toast.success(`Discount applied: ${CURRENCY.symbol}${response.data.discountAmount?.toFixed(2) || '0'} off`);
        setShowDiscountDialog(false);

        // Update the current discount state
        setCurrentDiscount({
          type: discountType,
          value: parseFloat(discountValue),
          amount: response.data.discountAmount,
          reason: discountReason
        });

        // Refresh payment info if in payment popup
        if (pendingPaymentOrderId) {
          try {
            const paymentStatusResponse = await api.get<PaymentStatusResponse>(
              `/orders/${pendingPaymentOrderId}/check-payment-status`
            );
            setPaymentInfo(paymentStatusResponse.data);
          } catch (e) {
            console.log('Error refreshing payment info');
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to apply discount:', error);
      const message = error.response?.data?.error || 'Failed to apply discount';
      toast.error(message);
      setDiscountError(message); // persists inline next to the Apply button
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Handle removing discount
  const handleRemoveDiscount = async () => {
    if (!discountOrderLineId) {
      toast.error('Order line not found');
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError(null); // retry clears the previous inline error
    try {
      await api.delete(`/api/order-lines/${discountOrderLineId}/discount`);
      toast.success('Discount removed');
      setCurrentDiscount(null);
      setShowDiscountDialog(false);

      // Refresh payment info if in payment popup
      if (pendingPaymentOrderId) {
        try {
          const paymentStatusResponse = await api.get<PaymentStatusResponse>(
            `/orders/${pendingPaymentOrderId}/check-payment-status`
          );
          setPaymentInfo(paymentStatusResponse.data);
        } catch (e) {
          console.log('Error refreshing payment info');
        }
      }
    } catch (error: any) {
      console.error('Failed to remove discount:', error);
      const message = error.response?.data?.error || 'Failed to remove discount';
      toast.error(message);
      setDiscountError(message); // persists inline next to the Remove button
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Handle payment confirmation - proceed with COMPLETED status and confirm payment
  const handleConfirmPaymentAndComplete = async () => {
    // Double-click-proof: ignore while a confirmation is already in flight
    if (!pendingPaymentOrderId || isConfirmingPayment) return;

    const orderId = pendingPaymentOrderId;
    const paymentMethod = selectedPaymentMethod;
    setIsConfirmingPayment(true);
    setPaymentError(null); // retry clears the previous inline error

    // Mark this order as being updated
    setUpdatingOrders(prev => new Set(prev).add(orderId));

    const currentOrder = orders.find(o => o.id === orderId);

    try {
      // Update status to COMPLETED with payment confirmation and payment method
      const backendStatus = mapStatusToBackend('COMPLETED');
      const response = await api.put<Order>(`/orders/${orderId}/status`, {
        status: backendStatus,
        paymentConfirmed: true,
        paymentMethod: paymentMethod
      });

      // Close the popup only once the server has confirmed
      setShowPaymentConfirmation(false);
      setPaymentInfo(null);
      setPendingPaymentOrderId(null);
      setSelectedPaymentMethod(''); // Reset to empty (force selection)

      // Update local state immediately
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order =>
          order.id === orderId
            ? { ...order, status: 'COMPLETED', updatedAt: new Date().toISOString() }
            : order
        );
        return updatedOrders;
      });

      // Update statistics immediately
      setStatistics(prevStats => {
        const newStats = { ...prevStats };
        const previousStatus = currentOrder?.status;

        if (previousStatus && previousStatus !== 'COMPLETED') {
          if (previousStatus in newStats) {
            newStats[previousStatus as keyof typeof newStats] = Math.max(0, (newStats[previousStatus as keyof typeof newStats] as number) - 1);
          }
          newStats.COMPLETED = (newStats.COMPLETED as number) + 1;
        }

        return newStats;
      });

      const displayOrderId = currentOrder?.customOrderId || currentOrder?.orderNumber;
      toast.success(`Order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} completed and payment confirmed!`);

      // Background refresh for consistency
      setTimeout(async () => {
        try {
          await Promise.all([
            fetchOrderStatistics(),
            fetchVendorOrders(0, false)
          ]);
        } catch (error) {
          console.error('Background refresh failed:', error);
        }
      }, 1000);

    } catch (err: unknown) {
      console.error('Failed to complete order with payment confirmation:', err);
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Failed to complete order.';
      toast.error(errorMessage);
      // Keep the popup open and persist the error next to the Paid button
      setPaymentError(errorMessage);
    } finally {
      setIsConfirmingPayment(false);
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Handle cancellation with reason from the modal
  const handleCancelOrderWithReason = async () => {
    // Double-click-proof: ignore while a cancellation is already in flight
    if (!cancelOrderId || isCancellingOrder) return;

    // Mark this order as being updated; keep the modal open until the server acks
    setUpdatingOrders(prev => new Set(prev).add(cancelOrderId));
    setIsCancellingOrder(true);
    setCancelError(null); // retry clears the previous inline error

    console.log('🚀 Cancelling order:', { orderId: cancelOrderId, reason: cancellationReason, timestamp: new Date().toISOString() });

    try {
      const response = await api.put<Order>(`/orders/${cancelOrderId}/status`, {
        status: 'CANCELLED',
        cancellationReason: cancellationReason.trim() || undefined
      });
      console.log('✅ Order cancelled:', response.data);

      // Update local state immediately
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order =>
          order.id === cancelOrderId
            ? { ...order, status: 'CANCELLED', cancellationReason: cancellationReason.trim() || undefined, cancelledAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
            : order
        );
        return updatedOrders;
      });

      // Update statistics
      setStatistics(prevStats => {
        const newStats = { ...prevStats };
        const order = orders.find(o => o.id === cancelOrderId);
        const previousStatus = order?.status;
        if (previousStatus && previousStatus !== 'CANCELLED') {
          if (previousStatus in newStats) {
            newStats[previousStatus as keyof typeof newStats] = Math.max(0, (newStats[previousStatus as keyof typeof newStats] as number) - 1);
          }
          newStats.CANCELLED = (newStats.CANCELLED as number) + 1;
        }
        return newStats;
      });

      toast.success(`Order #${cancelOrderId} has been cancelled.`);

      // Success: close the modal and reset its state
      setIsCancelModalOpen(false);
      setCancelOrderId(null);
      setCancellationReason('');

      // Background refresh
      setTimeout(async () => {
        try {
          await Promise.all([
            fetchOrderStatistics(),
            fetchVendorOrders(0, false)
          ]);
        } catch (error) {
          console.error('Background refresh failed:', error);
        }
      }, 1000);

    } catch (err: unknown) {
      console.error('❌ Failed to cancel order:', err);
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Failed to cancel order.';
      toast.error(errorMessage);
      // Keep the modal open and persist the error next to the Cancel Order button
      setCancelError(errorMessage);
    } finally {
      setIsCancellingOrder(false);
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(cancelOrderId);
        return newSet;
      });
    }
  };

  // Performance monitoring - detects when clicks fail and auto-fixes
  const [clickPerformance, setClickPerformance] = useState<{
    totalClicks: number;
    successfulClicks: number;
    failedClicks: number;
    lastClickTime: number;
  }>({
    totalClicks: 0,
    successfulClicks: 0,
    failedClicks: 0,
    lastClickTime: 0
  });

  // Bulletproof click handler - ensures clicks always work
  const bulletproofClickHandler = useCallback((orderId: number, status: string, event: React.MouseEvent | React.TouchEvent | React.PointerEvent | React.KeyboardEvent) => {
    console.log('🎯 Bulletproof click handler triggered:', { orderId, status, eventType: event.type });

    // Track click performance
    setClickPerformance(prev => ({
      ...prev,
      totalClicks: prev.totalClicks + 1,
      lastClickTime: Date.now()
    }));

    // Only prevent default on specific events that need it (not mouse clicks)
    // Mouse clicks need to complete their natural sequence: mousedown -> mouseup -> click
    // Preventing default on mousedown breaks the click event on desktop browsers
    if (event.type !== 'mousedown' && event.type !== 'mouseup') {
      event.preventDefault();
      event.stopPropagation();
    }

    // For mouse events, only process on 'click' to avoid duplicate calls
    if ((event.type === 'mousedown' || event.type === 'mouseup') && event.type !== 'click') {
      console.log('⏭️ Skipping', event.type, 'event, waiting for click event');
      return;
    }

    // Check if already updating
    if (updatingOrders.has(orderId)) {
      console.log('⏳ Order', orderId, 'is already being updated, ignoring click');
      setClickPerformance(prev => ({
        ...prev,
        failedClicks: prev.failedClicks + 1
      }));
      return;
    }

    // Call the status update function
    console.log('🚀 Calling handleQuickStatusUpdate from bulletproof handler');

    try {
      handleQuickStatusUpdate(orderId, status);
      setClickPerformance(prev => ({
        ...prev,
        successfulClicks: prev.successfulClicks + 1
      }));
    } catch (error) {
      console.error('❌ Status update failed:', error);
      setClickPerformance(prev => ({
        ...prev,
        failedClicks: prev.failedClicks + 1
      }));
    }
  }, [updatingOrders, handleQuickStatusUpdate]);

  // Monitor click performance and auto-fix issues
  useEffect(() => {
    const performanceRatio = clickPerformance.totalClicks > 0
      ? clickPerformance.successfulClicks / clickPerformance.totalClicks
      : 1;

    console.log('📊 Click performance:', {
      total: clickPerformance.totalClicks,
      successful: clickPerformance.successfulClicks,
      failed: clickPerformance.failedClicks,
      ratio: performanceRatio.toFixed(2)
    });

    // If performance drops below 80%, show warning
    if (clickPerformance.totalClicks > 5 && performanceRatio < 0.8) {
      console.warn('⚠️ Click performance degraded:', performanceRatio.toFixed(2));
      toast.error('Click performance degraded. Refreshing page...');

      // Auto-refresh the page to fix any issues
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [clickPerformance]);

  // Click detection and retry mechanism
  const [clickAttempts, setClickAttempts] = useState<Map<number, number>>(new Map());

  const handleClickWithRetry = useCallback((orderId: number, status: string, event: any) => {
    const attemptCount = clickAttempts.get(orderId) || 0;
    console.log('🔄 Click attempt:', attemptCount + 1, 'for order:', orderId, 'status:', status);

    // Try the bulletproof handler
    try {
      bulletproofClickHandler(orderId, status, event);

      // If successful, reset attempt count
      setClickAttempts(prev => {
        const newMap = new Map(prev);
        newMap.delete(orderId);
        return newMap;
      });
    } catch (error) {
      console.error('❌ Click handler failed:', error);

      // Retry mechanism
      if (attemptCount < 3) {
        console.log('🔄 Retrying click for order:', orderId, 'attempt:', attemptCount + 1);
        setClickAttempts(prev => new Map(prev).set(orderId, attemptCount + 1));

        // Retry after a short delay
        setTimeout(() => {
          handleClickWithRetry(orderId, status, event);
        }, 100);
      } else {
        console.error('❌ Max retry attempts reached for order:', orderId);
        toast.error('Failed to update order status. Please try again.');
      }
    }
  }, [bulletproofClickHandler, clickAttempts]);

  // Close quick menu on outside click or Escape; compute viewport-aware placement
  useEffect(() => {
    if (markAsOpenForOrderId === null) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (markAsMenuRef.current && !markAsMenuRef.current.contains(target)) {
        setMarkAsOpenForOrderId(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMarkAsOpenForOrderId(null);
    };
    const recompute = () => {
      const btn = markAsTriggerRefs.current[markAsOpenForOrderId!];
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const desired = 260; // px
      const placementGuess = spaceBelow < desired && spaceAbove > spaceBelow ? 'above' : 'below';
      setMarkAsPlacement(placementGuess);
      setMarkAsAlignRight(window.innerWidth - r.left < 320);
      const available = placementGuess === 'below' ? spaceBelow - 16 : spaceAbove - 16;
      setMarkAsMaxHeight(Math.max(160, Math.min(320, available)));
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [markAsOpenForOrderId]);

  // Unified badge style used for filter pills (darker, more prominent)
  const getBadgeStyle = useCallback((status: string) => {
    // Use theme.components.status when available, with hardcoded fallbacks for backward compatibility
    const ts = theme.components?.status;
    const modernStyles: Record<string, { bg: string; color: string; border: string; shadow?: string }> = {
      PENDING: { bg: ts?.pending?.bg || '#FEF3C7', color: ts?.pending?.text || '#B45309', border: ts?.pending?.border || '#F59E0B', shadow: '0 2px 4px rgba(245, 158, 11, 0.2)' },
      ACCEPTED: { bg: ts?.accepted?.bg || '#DBEAFE', color: ts?.accepted?.text || '#1D4ED8', border: ts?.accepted?.border || '#3B82F6', shadow: '0 2px 4px rgba(59, 130, 246, 0.2)' },
      PREPARING: { bg: ts?.preparing?.bg || '#EDE9FE', color: ts?.preparing?.text || '#6D28D9', border: ts?.preparing?.border || '#8B5CF6', shadow: '0 2px 4px rgba(139, 92, 246, 0.2)' },
      READY: { bg: ts?.ready?.bg || '#D1FAE5', color: ts?.ready?.text || '#047857', border: ts?.ready?.border || '#10B981', shadow: '0 2px 4px rgba(16, 185, 129, 0.2)' },
      SERVED: { bg: ts?.served?.bg || '#E0E7FF', color: ts?.served?.text || '#4338CA', border: ts?.served?.border || '#6366F1', shadow: '0 2px 4px rgba(99, 102, 241, 0.2)' },
      COMPLETED: { bg: ts?.completed?.bg || '#D1FAE5', color: ts?.completed?.text || '#047857', border: ts?.completed?.border || '#10B981', shadow: '0 2px 4px rgba(16, 185, 129, 0.2)' },
      CANCELLED: { bg: ts?.cancelled?.bg || '#FEE2E2', color: ts?.cancelled?.text || '#B91C1C', border: ts?.cancelled?.border || '#EF4444', shadow: '0 2px 4px rgba(239, 68, 68, 0.2)' },
    };

    const style = modernStyles[status] || modernStyles.ACCEPTED;

    return {
      backgroundColor: style.bg,
      color: style.color,
      border: `1px solid ${style.border}`,
      fontWeight: 600 as const,
      ...(isModernTheme && style.shadow ? { boxShadow: style.shadow } : {}),
    };
  }, [isModernTheme, theme.components?.status]);

  // Lighter badge style for card status pills (same colors as filter pills but slightly lighter/softer)
  const getCardBadgeStyle = useCallback((status: string) => {
    // Use theme.components.status when available, with hardcoded fallbacks for backward compatibility
    const ts = theme.components?.status;
    const cardStyles: Record<string, { bg: string; color: string; border: string }> = {
      PENDING: { bg: ts?.pending?.bg || '#FEF9F0', color: ts?.pending?.text || '#D97706', border: ts?.pending?.border || '#FCD34D' },
      ACCEPTED: { bg: ts?.accepted?.bg || '#EFF6FF', color: ts?.accepted?.text || '#2563EB', border: ts?.accepted?.border || '#93C5FD' },
      PREPARING: { bg: ts?.preparing?.bg || '#F5F3FF', color: ts?.preparing?.text || '#7C3AED', border: ts?.preparing?.border || '#C4B5FD' },
      READY: { bg: ts?.ready?.bg || '#ECFDF5', color: ts?.ready?.text || '#059669', border: ts?.ready?.border || '#6EE7B7' },
      SERVED: { bg: ts?.served?.bg || '#EEF2FF', color: ts?.served?.text || '#4F46E5', border: ts?.served?.border || '#A5B4FC' },
      COMPLETED: { bg: ts?.completed?.bg || '#ECFDF5', color: ts?.completed?.text || '#059669', border: ts?.completed?.border || '#6EE7B7' },
      CANCELLED: { bg: ts?.cancelled?.bg || '#FEF2F2', color: ts?.cancelled?.text || '#DC2626', border: ts?.cancelled?.border || '#FCA5A5' },
    };

    const style = cardStyles[status] || cardStyles.ACCEPTED;

    return {
      backgroundColor: style.bg,
      color: style.color,
      border: `1px solid ${style.border}`,
      fontWeight: 600 as const,
    };
  }, [theme.components?.status]);

  // Function to fetch order statistics (total counts)
  const fetchOrderStatistics = useCallback(() => {
    if (!user) return;

    const vendorId = (user as any).vendorId || user.id;
    console.log('📊 Fetching order statistics for vendor:', vendorId, 'dateFilter:', dateFilter, 'timestamp:', new Date().toISOString());

    // Build query params with date filter and order type filters
    const params = new URLSearchParams();
    params.append('dateFilter', dateFilter);

    // Add order type filters
    if (orderTypeFilters.length > 0) {
      orderTypeFilters.forEach(type => params.append('orderTypes', type));
    }

    api.get<{ statistics: { [key: string]: number } }>(`/vendors/${vendorId}/orders/statistics?${params.toString()}`)
      .then(res => {
        const stats = res.data.statistics;
        console.log('📊 Order statistics received:', stats, 'timestamp:', new Date().toISOString());
        // Calculate ACTIVE count (all except COMPLETED and CANCELLED)
        const activeCount = (stats.PENDING || 0) + (stats.ACCEPTED || 0) + (stats.PREPARING || 0) + (stats.READY || 0) + (stats.SERVED || 0);
        setStatistics({
          TOTAL: stats.TOTAL || 0,
          ACTIVE: activeCount,
          PENDING: stats.PENDING || 0,
          ACCEPTED: stats.ACCEPTED || 0,
          PREPARING: stats.PREPARING || 0,
          READY: stats.READY || 0,
          SERVED: stats.SERVED || 0,
          COMPLETED: stats.COMPLETED || 0,
          CANCELLED: stats.CANCELLED || 0
        });
        console.log('📊 Statistics state updated:', {
          TOTAL: stats.TOTAL || 0,
          ACTIVE: activeCount,
          PENDING: stats.PENDING || 0,
          ACCEPTED: stats.ACCEPTED || 0,
          PREPARING: stats.PREPARING || 0,
          READY: stats.READY || 0,
          SERVED: stats.SERVED || 0,
          COMPLETED: stats.COMPLETED || 0,
          CANCELLED: stats.CANCELLED || 0
        });
      })
      .catch(err => {
        console.error("❌ Failed to fetch order statistics:", err);
        // Don't show error to user for statistics - it's non-critical
      });
  }, [user, dateFilter, orderTypeFilters]);

  // Function to fetch orders from the API
  const fetchVendorOrders = useCallback((page: number = 0, append: boolean = false) => {
    if (!user) {
      setError("User not authenticated. Cannot fetch vendor orders.");
      setOrders([]);
      setLoading(false);
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsFetching(true);
      // Only show full-page loader on initial load (no data yet)
      if (!hasLoadedOnce.current) {
        setLoading(true);
        pageLoadingStartedAtRef.current = Date.now();
      }
    }

    // Use vendorId if available (for vendor users), otherwise use user.id
    const vendorId = (user as any).vendorId || user.id;

    // Build query parameters
    const params = new URLSearchParams();
    params.append('dateFilter', dateFilter);
    params.append('page', page.toString());
    // Fetch more orders to cover grouping needs - only apply status filter on client-side for performance
    params.append('size', '500');
    params.append('sortBy', sortBy);
    params.append('sortDirection', sortDirection);
    // NOTE: We no longer send status filter to backend - we fetch all and filter client-side
    // This allows us to make ONE API call instead of TWO (one for filtered, one for grouping)
    // Add order type filters
    if (orderTypeFilters.length > 0) {
      orderTypeFilters.forEach(type => params.append('orderTypes', type));
    }

    console.log('Fetching vendor orders for vendor ID:', vendorId, 'with params:', params.toString());

    api.get<{ content: Order[], page: number, size: number, totalElements: number, totalPages: number } | Order[]>(`/vendors/${vendorId}/orders?${params}`)
      .then(res => {
        const response = res.data;

        // Handle both paginated response and direct array
        let allOrdersData: Order[];

        if (Array.isArray(response)) {
          // Direct array response (current backend behavior)
          allOrdersData = response;
          console.log("API responded with direct array:", allOrdersData.length, "orders");
        } else {
          // Paginated response (expected format)
          allOrdersData = Array.isArray(response.content) ? response.content : [];
          console.log("API responded with paginated orders:", allOrdersData.length, "orders");
        }

        // Store all orders for grouping (before filtering)
        setAllOrdersForGrouping(allOrdersData);

        // Apply client-side status filtering
        let filteredOrders = allOrdersData;
        if (statusFilter === 'ACTIVE') {
          filteredOrders = allOrdersData.filter(o => !['COMPLETED', 'CANCELLED'].includes(o.status));
        } else if (statusFilter) {
          filteredOrders = allOrdersData.filter(o => o.status === statusFilter);
        }

        // Apply pagination client-side
        const startIndex = page * pageSize;
        const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

        const paginationInfo = {
          page: page,
          size: paginatedOrders.length,
          totalElements: filteredOrders.length,
          totalPages: Math.ceil(filteredOrders.length / pageSize)
        };

        if (append) {
          // Append to existing orders
          setOrders(prevOrders => [...prevOrders, ...paginatedOrders]);
        } else {
          // Replace orders
          setOrders(paginatedOrders);

          // Only consider PENDING orders as current pending orders
          const currentPendingOrders = filteredOrders.filter(o => o.status === 'PENDING');

          if (expandedOrderId === null && currentPendingOrders.length > 0) {
            setExpandedOrderId(currentPendingOrders[0].id);
          } else if (expandedOrderId !== null && !filteredOrders.some(o => o.id === expandedOrderId)) {
            // Only reset expanded order if the order doesn't exist at all, not just because it's not pending
            setExpandedOrderId(currentPendingOrders.length > 0 ? currentPendingOrders[0].id : null);
          }
        }

        // Update pagination state
        setCurrentPage(paginationInfo.page);
        setTotalElements(paginationInfo.totalElements);
        setTotalPages(paginationInfo.totalPages);
        setError(null);
        hasLoadedOnce.current = true;
      })
      .catch((err: unknown) => {
        console.error("Failed to fetch vendor orders:", err);
        const error = err as { response?: { data?: { message?: string; isHtmlResponse?: boolean }, status?: number } };
        const errorMessage = error.response?.data?.message || 'Failed to fetch vendor orders.';

        // Check if this is an authentication error
        const isAuthError = error.response?.status === 401 ||
          errorMessage.includes('Authentication required') ||
          error.response?.data?.isHtmlResponse === true;

        if (isAuthError) {
          console.log('🚪 Authentication error detected - logging out and redirecting to login');
          // Clear auth state and redirect to login
          logout();
          navigate('/login', { replace: true });
          return;
        }

        setError(errorMessage);
        if (!append && !hasLoadedOnce.current) {
          setOrders([]);
        }
      })
      .finally(() => {
        // Respect minimum page-level loader duration only for initial load
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsFetching(false);
          if (pageLoadingStartedAtRef.current) {
            // Initial load — respect min delay
            const startedAt = pageLoadingStartedAtRef.current;
            const elapsed = Date.now() - startedAt;
            const remaining = Math.max(0, MIN_PAGE_LOADER_MS - elapsed);
            pageLoadingStartedAtRef.current = null;
            if (remaining > 0) {
              setTimeout(() => setLoading(false), remaining);
            } else {
              setLoading(false);
            }
          } else {
            // Subsequent filter change — instant
            setLoading(false);
          }
          setIsLoadingMore(false);
        }
        // Ensure the chip spinner is visible for at least MIN_SWITCH_SPINNER_MS
        if (switchingStatus) {
          const startedAt = switchingStartedAtRef.current || 0;
          const elapsed = Date.now() - startedAt;
          const remaining = MIN_SWITCH_SPINNER_MS - elapsed;
          if (remaining > 0) {
            setTimeout(() => setSwitchingStatus(null), remaining);
          } else {
            setSwitchingStatus(null);
          }
        }
      });
  }, [user, expandedOrderId, dateFilter, statusFilter, orderTypeFilters, pageSize, sortBy, sortDirection]);

  // NOTE: fetchAllOrdersForGrouping has been removed - we now fetch all orders in one call
  // and set allOrdersForGrouping directly in fetchVendorOrders (see above)
  // This reduces API calls from 2 to 1 for better performance

  // Update an order in the state when we receive an update
  const updateOrder = useCallback((updatedOrder: Order) => {
    // Ensure the order data has all required fields
    if (!updatedOrder || !updatedOrder.id) {
      console.error('❌ Invalid order data received:', updatedOrder);
      toast.error('Invalid order data received');
      return;
    }

    console.log('🔄 Processing order update for order ID:', updatedOrder.id, 'Status:', updatedOrder.status, 'Timestamp:', new Date().toISOString());

    // Handle the state update in a safe way
    setOrders(prevOrders => {
      // Make sure we're working with a proper array
      const currentOrders = Array.isArray(prevOrders) ? prevOrders : [];
      console.log('🔍 Current orders count:', currentOrders.length);
      console.log('🔍 Looking for order ID:', updatedOrder.id);

      // Create a completely new array to ensure React detects the change
      const updatedOrdersList = [...currentOrders];
      const existingOrderIndex = updatedOrdersList.findIndex(o => o.id === updatedOrder.id);
      console.log('🔍 Existing order index:', existingOrderIndex);

      if (existingOrderIndex !== -1) {
        // Update existing order
        console.log('✅ Updating existing order:', updatedOrder.id, 'at index:', existingOrderIndex);
        console.log('📊 Old status:', updatedOrdersList[existingOrderIndex].status);
        console.log('📊 New status:', updatedOrder.status);
        // Merge existing order data with update (keep customOrderId from existing if not in update)
        const mergedOrder = {
          ...updatedOrdersList[existingOrderIndex],  // Keep existing fields (including customOrderId)
          ...updatedOrder,                          // Overwrite with new data
        };
        updatedOrdersList[existingOrderIndex] = mergedOrder;
        console.log('✅ Order updated successfully');

        // Don't auto-collapse orders when they change status - let user control this
        // if (updatedOrder.id === expandedOrderId && updatedOrder.status !== 'PENDING') {
        //   // If the expanded order is no longer pending, try to expand the next pending one or collapse
        //   const currentPendingOrders = updatedOrdersList.filter(o => o.status === 'PENDING');
        //   setExpandedOrderId(currentPendingOrders.length > 0 ?
        //     currentPendingOrders.filter(o => o.id !== updatedOrder.id)[0]?.id ?? null : null);
        // }
        // Use merged order to ensure we have customOrderId from existing order if not in update
        toast.success(`Order ${formatOrderId(mergedOrder.customOrderId, DisplayContext.VENDOR)} status updated to ${mergedOrder.status}!`);
      } else {
        // Add new order to the beginning
        console.log('🆕 Adding new order:', updatedOrder.id);
        console.log('🔍 Order not found in current list, adding as new order');
        updatedOrdersList.unshift(updatedOrder);
        toast.success(`🎉 New order ${formatOrderId(updatedOrder.customOrderId || updatedOrder.orderNumber, DisplayContext.VENDOR)} received!`, {
          duration: 5000,
          style: {
            background: 'var(--action)',
            color: 'white',
          },
        });

        // Play sound for new order (if available)
        try {
          const audio = new Audio('/notification-sound.mp3');
          audio.volume = 0.7; // Set moderate volume
          audio.play().catch(() => console.log('Could not play notification sound'));
        } catch {
          console.log('Audio notification not supported or sound file missing');
        }

        // For new orders, expand it if it's pending and nothing else is currently expanded
        if (updatedOrder.status === 'PENDING' && expandedOrderId === null) {
          setExpandedOrderId(updatedOrder.id);
          console.log('Auto-expanding new pending order:', updatedOrder.id);
        }
      }

      console.log('Updated orders list now has', updatedOrdersList.length, 'orders');
      return updatedOrdersList;
    });

    // Also update allOrdersForGrouping to keep status summaries accurate
    setAllOrdersForGrouping(prevOrders => {
      const currentOrders = Array.isArray(prevOrders) ? prevOrders : [];
      const updatedOrdersList = [...currentOrders];
      const existingOrderIndex = updatedOrdersList.findIndex(o => o.id === updatedOrder.id);

      if (existingOrderIndex !== -1) {
        const mergedOrder = {
          ...updatedOrdersList[existingOrderIndex],
          ...updatedOrder,
        };
        updatedOrdersList[existingOrderIndex] = mergedOrder;
      } else {
        updatedOrdersList.unshift(updatedOrder);
      }

      return updatedOrdersList;
    });
  }, [expandedOrderId]);

  // Initial fetch and refetch on filter changes
  // Fetch orders when filters change or on initial load
  useEffect(() => {
    if (!user) return; // Wait for user to be available

    // Reset pagination when filters change
    setCurrentPage(0);
    fetchVendorOrders(0, false);
    // Fetch statistics separately (not affected by status filter or pagination)
    fetchOrderStatistics();
  }, [user, dateFilter, statusFilter, orderTypeFilters, sortBy, sortDirection, fetchOrderStatistics]);

  // Function to load more orders (for pagination)
  const loadMoreOrders = useCallback(() => {
    if (currentPage + 1 < totalPages && !isLoadingMore) {
      const nextPage = currentPage + 1;
      fetchVendorOrders(nextPage, true);
    }
  }, [currentPage, totalPages, isLoadingMore, fetchVendorOrders]);

  // Function to reset pagination and reload
  const resetAndReload = useCallback(() => {
    setCurrentPage(0);
    fetchVendorOrders(0, false);
  }, [fetchVendorOrders]);

  // Setup Socket.IO subscription for real-time updates
  useEffect(() => {
    // Define handler outside of condition so it can be referenced in cleanup
    const handleNotification = (notification: any) => {
      console.log('Socket.IO notification received in VendorOrders - RAW:', notification);

      // The notification IS the data directly, not wrapped in an object
      // Check if it's the ORDER_NEW event based on the notification content
      if (notification?.event === ORDER_EVENTS.NEW ||
        notification?.data?.event === ORDER_EVENTS.NEW ||
        notification?.type === ORDER_EVENTS.NEW) {

        console.log('ORDER_NEW detected! Showing toast now...');

        // Extract order info from various possible locations
        const data = notification?.data || notification;
        const orderId = data?.orderId || data?.order_id || data?.id;
        // Prefer customOrderId (readable format) over orderNumber (which might also be readable)
        const displayOrderId = data?.customOrderId || data?.orderNumber || orderId;

        // Show the toast immediately with readable order ID
        showToast({
          message: `🎉 New order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} received!`,
          kind: 'success',
          icon: '🎉',
          durationMs: 5000,
          idKey: `ORDER_NEW_${orderId}`, // Use numeric ID for deduplication
          allowedPaths: ['/vendor/orders']
        });

        // Play notification sound
        try {
          const audio = new Audio('/notification-sound.mp3');
          audio.volume = 0.7;
          audio.play().catch(() => console.log('Could not play notification sound'));
        } catch {
          console.log('Audio notification not supported');
        }

        // Refresh orders AND statistics so tab counters reflect the new order immediately
        resetAndReload();
        fetchOrderStatistics();
      } else if (notification?.event || notification?.type) {
        // Handle other events
        const event = notification?.event || notification?.type;
        console.log(`Other event received: ${event}`);

        if (['ORDER_UPDATE', 'ORDER_STATUS_UPDATE', 'kotCreated'].includes(event)) {
          resetAndReload();
          fetchOrderStatistics();
        }
      }
    };

    const currentVendorId = (user as any)?.vendorId || user?.id;
    if (connected && currentVendorId) {
      console.log(`Vendor subscribing to Socket.IO notifications for vendor ${currentVendorId}`);

      // Subscribe to vendor room
      subscribeToVendor(currentVendorId);

      // Listen for notification events using standardized constant
      on(NOTIFICATION_EVENTS.GENERAL, handleNotification);

      // Also listen for specific ORDER_NEW event directly in case it comes separately
      const handleNewOrder = (data: any) => {
        console.log('[VendorOrders] ORDER_NEW event received directly:', data);
        const orderId = data?.orderId || data?.order_id || data?.id;
        // Prefer customOrderId (readable format like D-GH-241203-0001)
        const displayOrderId = data?.customOrderId || data?.orderNumber || orderId;
        showToast({
          message: `🎉 New order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} received!`,
          kind: 'success',
          icon: '🎉',
          durationMs: 5000,
          // PAGE suffix ensures at least one toast shows even if another handler dedupes
          idKey: `ORDER_NEW_PAGE_${orderId}`, // Use numeric ID for deduplication
          allowedPaths: ['/vendor/orders']
        });
        resetAndReload();
        fetchOrderStatistics(); // Refresh tab counters on new order
      };

      on(ORDER_EVENTS.NEW, handleNewOrder);

      // Listen for order status updates (triggered when KOT status changes affect order status)
      const handleOrderStatusUpdate = (data: any) => {
        console.log('[VendorOrders] ORDER_STATUS_UPDATE event received:', data);
        const orderId = data?.orderId || data?.order_id || data?.id;
        const newStatus = data?.status || data?.newStatus;

        if (orderId && newStatus) {
          console.log(`[VendorOrders] Order #${orderId} status changed to ${newStatus}`);

          // Update the order locally for immediate UI feedback
          setOrders(prevOrders => {
            const updatedOrders = prevOrders.map(order => {
              if (order.id === orderId) {
                return { ...order, status: newStatus, updatedAt: new Date().toISOString() };
              }
              return order;
            });
            return updatedOrders;
          });

          // Also update allOrdersForGrouping
          setAllOrdersForGrouping(prevOrders => {
            const updatedOrders = prevOrders.map(order => {
              if (order.id === orderId) {
                return { ...order, status: newStatus, updatedAt: new Date().toISOString() };
              }
              return order;
            });
            return updatedOrders;
          });

          // Also refresh statistics
          fetchOrderStatistics();
        }
      };

      on(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);

      // Cleanup on unmount or when dependencies change
      return () => {
        console.log('Vendor unsubscribing from Socket.IO');
        unsubscribeFromVendor(currentVendorId);
        off(NOTIFICATION_EVENTS.GENERAL, handleNotification);
        off(ORDER_EVENTS.NEW, handleNewOrder);
        off(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);
      };
    }
  }, [connected, user, subscribeToVendor, unsubscribeFromVendor, on, off, resetAndReload]);


  // Refresh orders when the tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, refreshing orders');
        resetAndReload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [resetAndReload]);

  // Legacy handler no longer used
  // const handleMarkAsCompleted = (orderId: number) => {};

  // Modal functions
  const openOrderModal = async (order: Order) => {
    setSelectedOrder(order);
    setModalLoading(true);
    setOrderAnalytics(null);
    setOrderKOTs([]);

    try {
      // Fetch fresh order data, analytics data and KOTs for the order in parallel
      console.log('Fetching order details, analytics and KOTs for order:', order.id);
      const vendorId = (user as any)?.vendorId || user?.id;

      const [orderResponse, analyticsResponse, kotsResponse] = await Promise.allSettled([
        api.get<Order>(`/orders/${order.id}`), // Fetch fresh order data to get latest cancellation reason
        api.get<OrderAnalytics>(`/vendors/${vendorId}/orders/${order.id}/analytics`),
        api.get<KOT[]>(`/api/kot/order/${order.id}`)
      ]);

      // Update selected order with fresh data (including cancellation reason)
      if (orderResponse.status === 'fulfilled') {
        setSelectedOrder(orderResponse.value.data);
        // Also update the order in the main orders list
        setOrders(prevOrders =>
          prevOrders.map(o =>
            o.id === order.id
              ? { ...o, ...orderResponse.value.data }
              : o
          )
        );
      }

      if (analyticsResponse.status === 'fulfilled') {
        console.log('Analytics response:', analyticsResponse.value.data);
        setOrderAnalytics(analyticsResponse.value.data);
      } else {
        console.error("Failed to fetch order analytics:", analyticsResponse.reason);
        toast.error("Could not load time analytics for this order");
      }

      if (kotsResponse.status === 'fulfilled') {
        console.log('KOTs response:', kotsResponse.value.data);
        setOrderKOTs(kotsResponse.value.data);
      } else {
        console.error("Failed to fetch order KOTs:", kotsResponse.reason);
        // Don't show error for KOTs as it's not critical
      }
    } catch (err: unknown) {
      console.error("Failed to fetch order data:", err);
      toast.error("Could not load order details");
    } finally {
      setModalLoading(false);
    }
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setOrderAnalytics(null);
    setOrderKOTs([]);
    setExpandedKOTs(new Set());
    setModalLoading(false);
  };

  const handleKOTStatusUpdate = async (kotId: number, newStatus: string) => {
    setUpdatingKOTs(prev => new Set(prev).add(kotId));

    try {
      await api.put(`/api/kot/${kotId}/status`, { status: newStatus });

      // Update the KOT in local state
      setOrderKOTs(prevKOTs =>
        prevKOTs.map(kot =>
          kot.id === kotId
            ? { ...kot, status: newStatus }
            : kot
        )
      );

      // Refetch the order details to get updated order status
      if (selectedOrder) {
        try {
          const orderResponse = await api.get<Order>(`/orders/${selectedOrder.id}`);
          setSelectedOrder(orderResponse.data);

          // Also update the order in the main orders list with all fields including cancellation reason
          setOrders(prevOrders =>
            prevOrders.map(order =>
              order.id === selectedOrder.id
                ? { 
                    ...order, 
                    status: orderResponse.data.status, 
                    updatedAt: orderResponse.data.updatedAt,
                    cancellationReason: orderResponse.data.cancellationReason,
                    cancelledAt: orderResponse.data.cancelledAt
                  }
                : order
            )
          );
        } catch (error) {
          console.error('Error refetching order details:', error);
        }
      }

      console.log(`KOT ${kotId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating KOT status:', error);
      toast.error('Failed to update KOT status');
    } finally {
      setUpdatingKOTs(prev => {
        const updated = new Set(prev);
        updated.delete(kotId);
        return updated;
      });
    }
  };

  const handleStatusUpdateInModal = async (orderId: number, newStatus: string, options?: { paymentConfirmed?: boolean }) => {
    setModalLoading(true);
    console.log('Modal status update:', { orderId, newStatus, currentStatus: selectedOrder?.status, options });

    try {
      // Map frontend status to backend status (ACKNOWLEDGED → ACCEPTED)
      const backendStatus = mapStatusToBackend(newStatus);
      console.log('Modal: Mapped status for backend:', { frontend: newStatus, backend: backendStatus });

      // Build request body - include paymentConfirmed if provided
      const requestBody: { status: string; paymentConfirmed?: string } = { status: backendStatus };
      if (options?.paymentConfirmed) {
        requestBody.paymentConfirmed = 'true';
      }

      const response = await api.put<Order>(`/orders/${orderId}/status`, requestBody);
      console.log('Modal API response received:', response.data);

      // Update local state immediately
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order =>
          order.id === orderId
            ? { ...order, status: newStatus, updatedAt: new Date().toISOString() }
            : order
        );
        console.log('Modal: Orders list updated, new status for order', orderId, ':', newStatus);
        return updatedOrders;
      });

      // Update selected order immediately
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, status: newStatus, updatedAt: new Date().toISOString() });
      }

      // Update statistics immediately
      setStatistics(prevStats => {
        const newStats = { ...prevStats };
        const previousStatus = selectedOrder?.status;

        if (previousStatus && previousStatus !== newStatus) {
          // Decrease count for previous status
          if (previousStatus in newStats) {
            newStats[previousStatus as keyof typeof newStats] = Math.max(0, (newStats[previousStatus as keyof typeof newStats] as number) - 1);
          }

          // Increase count for new status
          if (newStatus in newStats) {
            newStats[newStatus as keyof typeof newStats] = (newStats[newStatus as keyof typeof newStats] as number) + 1;
          }
        }

        console.log('Modal: Statistics updated immediately:', newStats);
        return newStats;
      });

      // Use customOrderId from selectedOrder for display
      const displayOrderId = selectedOrder?.customOrderId || selectedOrder?.orderNumber;
      toast.success(`Order ${formatOrderId(displayOrderId, DisplayContext.VENDOR)} status updated to ${newStatus.replace('_', ' ')}!`);

      // Background refresh for consistency
      setTimeout(async () => {
        try {
          console.log('Modal: Background refresh for consistency...');
          await Promise.all([
            fetchOrderStatistics(),
            fetchVendorOrders(0, false)
          ]);
        } catch (error) {
          console.error('Modal: Background refresh failed:', error);
        }
      }, 1000);

    } catch (err: unknown) {
      console.error("Failed to update order status:", err);
      const error = err as { response?: { data?: { message?: string }, status?: number } };
      const errorMessage = error.response?.data?.message || "An unexpected error occurred. Please try again.";
      toast.error(errorMessage);
    } finally {
      setModalLoading(false);
    }
  };

  // Calculate total for orders if not available
  const calculateOrderTotal = (order: Order): number => {
    if (order.total) return order.total;

    return order.items.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);
  };

  // Make sure orders is treated as an array
  const safeOrders = Array.isArray(orders) ? orders : [];

  // Add total to orders if not available from API
  const ordersWithTotal = safeOrders.map(order => ({
    ...order,
    total: calculateOrderTotal(order)
  }));

  // Build queue positions for pending orders (createdAt ascending) - only for current page
  const pendingSortedAsc = [...ordersWithTotal]
    .filter(o => o.status === 'PENDING')
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const pendingPositionById = new Map<number, number>(pendingSortedAsc.map((o, i) => [o.id, i + 1]));

  // Orders are already filtered and sorted by the backend, just add position info
  // Also apply client-side filtering for ACTIVE status (in case backend doesn't support multi-status filter)
  const filteredOrders = statusFilter === 'ACTIVE'
    ? ordersWithTotal.filter(o => !['COMPLETED', 'CANCELLED'].includes(o.status))
    : ordersWithTotal;

  const sortedOrders = filteredOrders
    .map(o => (o.status === 'PENDING' ? { ...o, position: pendingPositionById.get(o.id) ?? undefined } : o));

  // Debug what's going into the render
  console.log(`Displaying ${sortedOrders.length} orders from page ${currentPage + 1} of ${totalPages} (${totalElements} total)`);

  // Sort options for dropdown
  const sortOptions = [
    { value: 'queuePosition', label: 'Queue Position', description: 'First in queue appears first' },
    { value: 'createdAt', label: 'Latest First', description: 'Most recent orders first' },
    { value: 'urgency', label: 'Urgency', description: 'Most urgent orders first' },
    { value: 'status', label: 'Status', description: 'Group by workflow stage' },
    { value: 'total', label: 'Order Value', description: 'Sort by order amount' },
    { value: 'customer', label: 'Customer Name', description: 'Alphabetical by customer' },
  ];

  const handleSortChange = (newSortBy: string, newSortDirection: string) => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  const renderOrderCard = (order: Order) => {
    const isExpanded = order.id === expandedOrderId;

    // Use lighter badge style for card status badges
    const getStatusColor = (status: string) => getCardBadgeStyle(status);

    // Get status color for modern theme - uses theme.components.status with fallbacks
    const getModernStatusColor = (status: string) => {
      const ts = theme.components?.status;
      const colors: Record<string, string> = {
        PENDING: ts?.pending?.text || '#D97706',
        ACCEPTED: ts?.accepted?.text || '#2563EB',
        PREPARING: ts?.preparing?.text || '#7C3AED',
        READY: ts?.ready?.text || '#059669',
        SERVED: ts?.served?.text || '#4F46E5',
        COMPLETED: ts?.completed?.text || '#059669',
        CANCELLED: ts?.cancelled?.text || '#DC2626',
      };
      return colors[status] || colors.PENDING;
    };

    // Get pastel background for card status badges - uses theme.components.status with fallbacks
    const getModernStatusBg = (status: string) => {
      const ts = theme.components?.status;
      const colors: Record<string, string> = {
        PENDING: ts?.pending?.bg || '#FEF9F0',
        ACCEPTED: ts?.accepted?.bg || '#EFF6FF',
        PREPARING: ts?.preparing?.bg || '#F5F3FF',
        READY: ts?.ready?.bg || '#ECFDF5',
        SERVED: ts?.served?.bg || '#EEF2FF',
        COMPLETED: ts?.completed?.bg || '#ECFDF5',
        CANCELLED: ts?.cancelled?.bg || '#FEF2F2',
      };
      return colors[status] || colors.PENDING;
    };

    // Get order type emoji for modern theme
    const getOrderTypeEmoji = (customOrderId: string | undefined) => {
      if (!customOrderId) return '🍽️';
      if (customOrderId.startsWith('D-')) return '🍽️'; // Dine-in
      if (customOrderId.startsWith('T-')) return '🥡'; // Takeaway
      if (customOrderId.startsWith('DL-')) return '🚗'; // Delivery
      if (customOrderId.startsWith('C-')) return '🔔'; // Counter
      return '📋';
    };

    // Get customer initial for avatar
    const getCustomerInitial = () => {
      const name = order.customerName || order.user?.username || 'C';
      return name.charAt(0).toUpperCase();
    };

    return (
      <div 
        key={order.id} 
        className="rounded-2xl mb-4 transition-all card-responsive overflow-hidden"
        style={{
          backgroundColor: theme.components?.card?.background || '#ffffff',
          color: theme.text,
          border: `1px solid ${theme.components?.card?.border || '#E5E7EB'}`,
          boxShadow: theme.components?.card?.shadow || '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
        }}>
        
        {/* Modern Theme: Clean Header */}
        {isModernTheme && (
          <div className="px-3 sm:px-4 py-2 sm:py-3">
            {/* Desktop layout - single row */}
            <div className="hidden sm:flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Order Type Emoji Circle */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${getOrderTypeDisplay(order.customOrderId).color} 0%, ${getOrderTypeDisplay(order.customOrderId).color}DD 100%)`,
                  }}
                >
                  <span className="text-xl">{getOrderTypeEmoji(order.customOrderId)}</span>
                </div>

                {/* Order ID and Type */}
                <div className="min-w-0">
                  <h3 className="text-lg font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
                    Order {formatOrderId(order.customOrderId, DisplayContext.VENDOR)}
                    {updatingOrders.has(order.id) && (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-600 ml-2">
                        <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </span>
                    )}
                  </h3>
                  <span
                    className="px-2.5 py-0.5 text-xs font-semibold rounded-full inline-flex items-center gap-1 mt-1"
                    style={{
                      backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15`,
                      color: getOrderTypeDisplay(order.customOrderId).color,
                    }}
                  >
                    {getOrderTypeDisplay(order.customOrderId).icon} {getOrderTypeDisplay(order.customOrderId).label}
                  </span>
                </div>
              </div>

              {/* Status Badge & Expand Button */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex flex-col items-end gap-1">
                  <span
                    className="px-3 py-1 text-xs font-semibold rounded-md uppercase whitespace-nowrap"
                    style={getCardBadgeStyle(order.status)}
                  >
                    {order.status === 'PENDING' ? '⏳ ' :
                      order.status === 'COMPLETED' ? '✅ ' :
                        order.status === 'CANCELLED' ? '❌ ' :
                          order.status === 'ACCEPTED' ? '👍 ' :
                            order.status === 'PREPARING' ? '👨‍🍳 ' :
                              order.status === 'READY' ? '🔔 ' :
                                order.status === 'SERVED' ? '🍽️ ' : ''}{order.status.replace('_', ' ')}
                  </span>
                  <div className="font-semibold text-base whitespace-nowrap" style={{ color: theme.text }}>
                    Total: {formatCurrency(order.finalTotal ?? order.total ?? 0)}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpandedOrderId(prevId => prevId === order.id ? null : order.id);
                  }}
                  className="flex items-center justify-center w-10 h-10 transition-all hover:bg-gray-200 rounded-lg"
                  style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-sunken)', border: 'none' }}
                  title={isExpanded ? 'Hide Details' : 'View Details'}
                >
                  {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Mobile layout - stacked similar to table cards */}
            <div className="sm:hidden">
              <div className="flex items-start gap-3">
                {/* Order Type Emoji Circle */}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${getOrderTypeDisplay(order.customOrderId).color} 0%, ${getOrderTypeDisplay(order.customOrderId).color}DD 100%)`,
                  }}
                >
                  <span className="text-lg">{getOrderTypeEmoji(order.customOrderId)}</span>
                </div>

                {/* Content area */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
                        Order {formatOrderId(order.customOrderId, DisplayContext.VENDOR, { includeDate: false })}
                        {updatingOrders.has(order.id) && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 ml-1">
                            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          </span>
                        )}
                      </h3>
                      <span
                        className="px-2 py-0.5 text-xs font-semibold rounded-full inline-flex items-center gap-1 mt-1"
                        style={{
                          backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15`,
                          color: getOrderTypeDisplay(order.customOrderId).color,
                        }}
                      >
                        {getOrderTypeDisplay(order.customOrderId).icon} {getOrderTypeDisplay(order.customOrderId).label}
                      </span>
                    </div>
                    {/* Status and total - right side */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className="px-2 py-0.5 text-xs font-semibold rounded-md uppercase whitespace-nowrap"
                          style={getCardBadgeStyle(order.status)}
                        >
                          {order.status === 'PENDING' ? '⏳ ' :
                            order.status === 'COMPLETED' ? '✅ ' :
                              order.status === 'CANCELLED' ? '❌ ' :
                                order.status === 'ACCEPTED' ? '👍 ' :
                                  order.status === 'PREPARING' ? '👨‍🍳 ' :
                                    order.status === 'READY' ? '🔔 ' :
                                      order.status === 'SERVED' ? '🍽️ ' : ''}{order.status.replace('_', ' ')}
                        </span>
                        <div className="font-bold text-sm whitespace-nowrap" style={{ color: theme.text }}>
                          Total: {formatCurrency(order.finalTotal ?? order.total ?? 0)}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExpandedOrderId(prevId => prevId === order.id ? null : order.id);
                        }}
                        className="flex items-center justify-center w-8 h-8 transition-all hover:bg-gray-200 rounded-lg"
                        style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-sunken)', border: 'none' }}
                        title={isExpanded ? 'Hide Details' : 'View Details'}
                      >
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Card Content */}
        <div className="p-responsive-md">
          {/* Classic Theme Header - matching table card layout */}
          {!isModernTheme && (
            <>
              {/* Desktop layout */}
              <div className="hidden sm:flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15` }}
                  >
                    <ShoppingBagIcon className="w-5 h-5" style={{ color: getOrderTypeDisplay(order.customOrderId).color }} />
                  </div>
                  <div className="min-w-0">
                    <span className="text-base font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                      Order {formatOrderId(order.customOrderId, DisplayContext.VENDOR)}
                      {updatingOrders.has(order.id) && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                          backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15`,
                          color: getOrderTypeDisplay(order.customOrderId).color
                        }}
                      >
                        {getOrderTypeDisplay(order.customOrderId).label}
                      </span>
                      <span className="text-sm" style={{ color: theme.gray[500] }}>
                        {order.customerName || (order.user ? order.user.username : 'Customer')}
                      </span>
                      <span className="text-xs" style={{ color: theme.gray[400] }}>
                        {formatRelativeTime(order.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className="px-2 py-0.5 text-xs font-semibold rounded-md uppercase whitespace-nowrap"
                      style={getCardBadgeStyle(order.status)}
                    >
                      {order.status === 'PENDING' ? '⏳ ' :
                        order.status === 'COMPLETED' ? '✅ ' :
                          order.status === 'CANCELLED' ? '❌ ' :
                            order.status === 'ACCEPTED' ? '👍 ' :
                              order.status === 'PREPARING' ? '👨‍🍳 ' :
                                order.status === 'READY' ? '🔔 ' :
                                  order.status === 'SERVED' ? '🍽️ ' : ''}{order.status.replace('_', ' ')}
                    </span>
                    <div className="font-bold text-base whitespace-nowrap" style={{ color: theme.text }}>
                      Total: {formatCurrency(order.finalTotal ?? order.total ?? 0)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedOrderId(prevId => prevId === order.id ? null : order.id);
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded-lg transition-all hover:bg-gray-100"
                    style={{ color: theme.gray[600], backgroundColor: 'transparent', border: 'none' }}
                    title={isExpanded ? 'Hide Details' : 'View Details'}
                  >
                    {isExpanded ? <ChevronUpIcon className="w-6 h-6" /> : <ChevronDownIcon className="w-6 h-6" />}
                  </button>
                </div>
              </div>

              {/* Mobile layout - matching table card layout */}
              <div className="sm:hidden mb-3">
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15` }}
                  >
                    <ShoppingBagIcon className="w-5 h-5" style={{ color: getOrderTypeDisplay(order.customOrderId).color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-base font-semibold" style={{ color: theme.text }}>
                          Order {formatOrderId(order.customOrderId, DisplayContext.VENDOR, { includeDate: false })}
                          {updatingOrders.has(order.id) && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 ml-1">
                              <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap"
                            style={{
                              backgroundColor: `${getOrderTypeDisplay(order.customOrderId).color}15`,
                              color: getOrderTypeDisplay(order.customOrderId).color
                            }}
                          >
                            {getOrderTypeDisplay(order.customOrderId).label}
                          </span>
                          <span className="text-xs whitespace-nowrap" style={{ color: theme.gray[500] }}>
                            {formatRelativeTime(order.createdAt)}
                          </span>
                        </div>
                      </div>
                      {/* Status and total - right side */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex flex-col items-end gap-1">
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded-md uppercase whitespace-nowrap"
                            style={getCardBadgeStyle(order.status)}
                          >
                            {order.status === 'PENDING' ? '⏳ ' :
                              order.status === 'COMPLETED' ? '✅ ' :
                                order.status === 'CANCELLED' ? '❌ ' :
                                  order.status === 'ACCEPTED' ? '👍 ' :
                                    order.status === 'PREPARING' ? '👨‍🍳 ' :
                                      order.status === 'READY' ? '🔔 ' :
                                        order.status === 'SERVED' ? '🍽️ ' : ''}{order.status.replace('_', ' ')}
                          </span>
                          <div className="font-medium text-sm whitespace-nowrap" style={{ color: theme.text }}>
                            Total: {formatCurrency(order.finalTotal ?? order.total ?? 0)}
                          </div>
                        </div>
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-100"
                          style={{ color: theme.gray[600] }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedOrderId(prevId => prevId === order.id ? null : order.id);
                          }}
                        >
                          {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Modern Theme: Customer & Location Info */}
          {isModernTheme && (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {/* Customer Avatar */}
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: '#6B7280' }} /* basil-todo */
                >
                  {getCustomerInitial()}
                </div>
                <span className="font-medium text-gray-700">
                  {order.customerName || (order.user ? order.user.username : 'Dine-in Customer')}
                </span>
              </div>
              
              {order.isStaffOrder && order.placedByName && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-700">
                  👤 Placed by: {order.placedByName}
                </span>
              )}
              
              {/* Table/Location Badge */}
              {(order.floorName || order.sectionName || order.tableName) && (
                <span 
                  className="px-2.5 py-1 text-xs font-medium rounded-md"
                  style={{ backgroundColor: 'var(--bg-sunken)', color: 'var(--text-tertiary)', border: '1px solid var(--border-subtle)' }}
                >
                  📍 {[order.floorName, order.sectionName, order.tableName ? `Table ${order.tableName}` : null]
                    .filter(Boolean)
                    .join(' → ')}
                </span>
              )}
            </div>
          )}


        {/* Cancellation reason if order is cancelled - show for all cancelled orders */}
        {order.status === 'CANCELLED' && (
          <div
            className="mt-2 p-2 rounded-lg text-sm"
            style={{ backgroundColor: `${theme.danger}15`, border: `1px solid ${theme.danger}30` }}
          >
            <span className="font-medium" style={{ color: theme.danger }}>Reason: </span>
            <span style={{ color: order.cancellationReason ? theme.textSecondary : theme.textMuted }}>
              {order.cancellationReason || 'No reason provided'}
            </span>
          </div>
        )}

        {/* Bottom row - only show for modern theme or when there's extra info */}
        {(isModernTheme ||
          (order.status === 'PENDING' && order.position != null && order.position > 0) ||
          (order.discountAmount != null && order.discountAmount > 0) ||
          (order.orderLineId != null && order.sessionTotalAmount != null && order.sessionTotalAmount !== order.total)
        ) && (
          <div className="flex items-center gap-2 mt-2">
            <div className="text-sm flex items-center flex-wrap gap-2" style={{ color: theme.gray[500] }}>
              {isModernTheme && (
                <span className="text-gray-500">
                  🕐 {formatRelativeTime(order.createdAt)} • {new Date(order.createdAt).toLocaleTimeString()}
                </span>
              )}
              {order.status === 'PENDING' && order.position != null && order.position > 0 && (
                <span
                  className={`font-bold ${isModernTheme ? 'px-2.5 py-1 rounded-md' : ''}`}
                  style={{
                    color: isModernTheme ? '#ffffff' : theme.primary,
                    backgroundColor: isModernTheme ? '#F59E0B' : 'transparent',
                  }}
                >
                  {isModernTheme ? '🎯 ' : ''}Queue: #{order.position}
                </span>
              )}
              {/* Show discount info if applicable */}
              {order.discountAmount != null && order.discountAmount > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--status-ok-bg)', color: 'var(--status-ok)' }}>
                  🏷️ -{CURRENCY.symbol}{order.discountAmount.toFixed(2)} saved
                </span>
              )}
              {/* Show table total if different from order total */}
              {order.orderLineId != null && order.sessionTotalAmount != null && order.sessionTotalAmount !== order.total && (() => {
                const qrCodeValue = typeof order.qrCode === 'string' ? order.qrCode : order.qrCode?.code;
                const isClickable = order.orderLineStatus === 'ACTIVE' && qrCodeValue;
                return isClickable ? (
                  <span
                    className="text-xs cursor-pointer hover:underline"
                    style={{ color: theme.primary }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/vendor/tables?table=${qrCodeValue}`);
                    }}
                    title="Click to view table details"
                  >
                    Table Total: {CURRENCY.symbol}{order.sessionTotalAmount.toFixed(2)}
                  </span>
                ) : (
                  <span className="text-xs" style={{ color: theme.gray[500] }}>
                    Table Total: {CURRENCY.symbol}{order.sessionTotalAmount.toFixed(2)}
                  </span>
                );
              })()}
            </div>
          </div>
        )}
        </div>
        {isExpanded && (
          <div className="mt-4 p-4 border-t border-gray-200">
            <h4 className="font-semibold">Order Details:</h4>

            {/* Order-level Special Instructions */}
            {order.orderNotes && (
              <div className="mt-2 mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Special Instructions:</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 italic mt-1">{order.orderNotes}</p>
              </div>
            )}

            {order.items && order.items.length > 0 ? (
              <div className="mt-2">
                {isModernTheme ? (
                  /* Modern Theme: Colorful item cards with emojis */
                  <div className="space-y-3 mt-3">
                    {order.items.map(item => {
                      const itemName = item.itemName || item.menuItem?.name || 'Unknown Item';
                      const itemStyle = getItemStyle(itemName);
                      const itemColor = itemStyle?.color || '#6B7280';
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-3 p-3 rounded-xl transition-all"
                          style={{
                            backgroundColor: `${itemColor}10`,
                            border: `1px solid ${itemColor}20`,
                          }}
                        >
                          {/* Item Image or Emoji */}
                          {(item.imageUrl || item.menuItem?.imageUrl) ? (
                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                              <MenuItemImage imageUrl={item.imageUrl || item.menuItem?.imageUrl} itemName={itemName} size="thumbnail" />
                            </div>
                          ) : (
                            <div
                              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                              style={{ background: itemStyle?.gradient || `linear-gradient(135deg, ${itemColor} 0%, ${itemColor}CC 100%)` }}
                            >
                              <span className="text-2xl">{itemStyle?.emoji || '🍽️'}</span>
                            </div>
                          )}
                          {/* Item Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-base" style={{ color: itemColor }}>
                                {itemName}
                              </span>
                              {item.portionName && (
                                <span
                                  className="px-2.5 py-1 text-xs rounded-full font-bold uppercase"
                                  style={{
                                    backgroundColor: itemColor,
                                    color: 'var(--text-inverse)',
                                  }}
                                >
                                  {item.portionName}
                                </span>
                              )}
                              {(item.requestedSpiceLevel || item.spiceLevel) && (
                                <SpiceLevelBadge
                                  spiceLevel={item.requestedSpiceLevel || item.spiceLevel}
                                  size="small"
                                />
                              )}
                            </div>
                            {item.comment && !item.comment.toLowerCase().includes('order placed at counter') && (
                              <p className="text-xs italic mt-1" style={{ color: theme.gray[500] }}>📝 {item.comment}</p>
                            )}
                            {item.toppings && item.toppings.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.toppings.map((topping, idx) => (
                                  <span
                                    key={typeof topping === 'string' ? idx : topping.id}
                                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                                    style={{ backgroundColor: `${itemColor}20`, color: itemColor }}
                                  >
                                    + {typeof topping === 'string' ? topping : topping.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Quantity & Price */}
                          <div className="text-right flex-shrink-0">
                            <span 
                              className="text-sm font-bold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${itemColor}20`, color: itemColor }}
                            >
                              x{item.quantity}
                            </span>
                            <div className="font-bold text-lg mt-1" style={{ color: itemColor }}>
                              {CURRENCY.symbol}{(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Classic Theme: Original list layout */
                  <ul className="list-disc pl-5 mt-2 text-sm">
                    {order.items.map(item => (
                      <li key={item.id} className="mb-1">
                        <span className="font-medium flex items-center gap-2">
                          {(item.imageUrl || item.menuItem?.imageUrl) && (
                            <span className="inline-block w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                              <MenuItemImage imageUrl={item.imageUrl || item.menuItem?.imageUrl} itemName={item.itemName || item.menuItem?.name || 'Unknown Item'} size="thumbnail" />
                            </span>
                          )}
                          {item.itemName || item.menuItem?.name || 'Unknown Item'}
                        </span>
                        {item.portionName && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded uppercase font-medium">
                            {item.portionName}
                          </span>
                        )}
                        {(item.requestedSpiceLevel || item.spiceLevel) && (
                          <span className="ml-2">
                            <SpiceLevelBadge
                              spiceLevel={item.requestedSpiceLevel || item.spiceLevel}
                              size="small"
                            />
                          </span>
                        )} x {item.quantity}
                        <span className="ml-2">{CURRENCY.symbol}{(item.price * item.quantity).toFixed(2)}</span>
                        {item.comment && !item.comment.toLowerCase().includes('order placed at counter') && (
                          <p className="text-xs italic mt-1">Note: {item.comment}</p>
                        )}
                        {item.toppings && item.toppings.length > 0 && (
                          <ul className="list-circle pl-5 text-xs mt-1">
                            {item.toppings.map((topping, idx) => (
                              <li key={typeof topping === 'string' ? idx : topping.id}>
                                + {typeof topping === 'string' ? topping : topping.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {order.deliveryType && (
                  <p className="mt-2 text-sm">
                    <span className="font-medium">Delivery Type:</span> {order.deliveryType.replace('_', ' ').toLowerCase()}
                  </p>
                )}
                {/* Location info is now shown in header as: Floor > Section > Table */}
                {!order.floorName && !order.sectionName && !order.tableName && order.qrCode?.location && (
                  <div className="text-sm mt-2">
                    <span className="font-medium">Location:</span> <span className="ml-2">{order.qrCode.location}</span>
                  </div>
                )}
                {/* Cancellation Reason - Show for cancelled orders */}
                {order.status === 'CANCELLED' && (
                  <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: `${theme.danger}15`, border: `1px solid ${theme.danger}40` }}>
                    <p className="text-sm font-medium" style={{ color: theme.danger }}>
                      Cancellation Reason:
                    </p>
                    <p className="text-sm mt-1" style={{ color: order.cancellationReason ? theme.text : theme.textMuted }}>
                      {order.cancellationReason || 'No reason provided'}
                    </p>
                    {order.cancelledAt && (
                      <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                        Cancelled on: {new Date(order.cancelledAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm italic mt-2">No item details available.</p>
            )}

            {/* Persistent inline error from the last failed status action on this order */}
            {orderActionErrors[order.id] && (
              <p role="alert" className="text-xs mt-2 font-medium" style={{ color: 'var(--status-bad)' }}>
                {orderActionErrors[order.id]}
              </p>
            )}

            {order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
              <div className="relative inline-block">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const btn = markAsTriggerRefs.current[order.id];
                    if (btn) {
                      const r = btn.getBoundingClientRect();
                      const spaceBelow = window.innerHeight - r.bottom;
                      const spaceAbove = r.top;
                      const desired = 260;
                      const placementGuess = spaceBelow < desired && spaceAbove > spaceBelow ? 'above' : 'below';
                      setMarkAsPlacement(placementGuess);
                      setMarkAsAlignRight(window.innerWidth - r.left < 320);
                      const available = placementGuess === 'below' ? spaceBelow - 16 : spaceAbove - 16;
                      setMarkAsMaxHeight(Math.max(160, Math.min(320, available)));
                    }
                    setMarkAsOpenForOrderId((prev) => (prev === order.id ? null : order.id));
                  }}
                  className="text-sm mt-2 inline-block font-semibold px-4 py-2 rounded-lg transition-all btn-size-responsive mr-3 shadow-sm"
                  style={{
                    backgroundColor: theme.primary,
                    color: 'var(--text-inverse)',
                    border: 'none',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  ref={(el) => (markAsTriggerRefs.current[order.id] = el)}
                >
                  Mark as
                </button>
                {markAsOpenForOrderId === order.id && (
                  <div
                    ref={(el) => (markAsMenuRef.current = el)}
                    className={`absolute ${markAsPlacement === 'below' ? 'top-full mt-2' : 'bottom-full mb-2'} ${markAsAlignRight ? 'right-0' : 'left-0'} w-64 rounded-md shadow-lg border z-50 p-2`}
                    style={{
                      backgroundColor: theme.components.card.background,
                      borderColor: theme.components.card.border,
                      pointerEvents: 'auto',
                      zIndex: 9999,
                      position: 'relative'
                    }}
                    onMouseEnter={() => console.log('🖱️ Mouse entered dropdown for order:', order.id)}
                    onMouseLeave={() => console.log('🖱️ Mouse left dropdown for order:', order.id)}
                    onClick={() => {
                      console.log('🖱️ Dropdown container clicked for order:', order.id);
                      // Don't stop propagation here - let button clicks work
                    }}
                  >
                    <div className="grid grid-cols-2 gap-1.5 overflow-auto" style={{ maxHeight: markAsMaxHeight }}>
                      {(() => {
                        const validStatuses = getValidNextStatuses(order.status);
                        console.log('🔍 Valid statuses for order', order.id, 'with status', order.status, ':', validStatuses);
                        console.log('🔍 Does validStatuses include CANCELLED?', validStatuses.includes('CANCELLED'));
                        return validStatuses;
                      })().map((status, statusIndex) => {
                        const ts = theme.components?.status;
                        const styles: Record<string, { bg: string; border: string; text: string; icon: string }> = {
                          PENDING: { bg: ts?.pending?.bg || '#FEF3C7', border: ts?.pending?.border || '#F59E0B', text: ts?.pending?.text || '#D97706', icon: '⏳' },
                          ACCEPTED: { bg: ts?.accepted?.bg || '#DBEAFE', border: ts?.accepted?.border || '#3B82F6', text: ts?.accepted?.text || '#2563EB', icon: '✅' },
                          PREPARING: { bg: ts?.preparing?.bg || '#FED7AA', border: ts?.preparing?.border || '#FB923C', text: ts?.preparing?.text || '#EA580C', icon: '👨‍🍳' },
                          READY: { bg: ts?.ready?.bg || '#D1FAE5', border: ts?.ready?.border || '#10B981', text: ts?.ready?.text || '#059669', icon: '🔔' },
                          SERVED: { bg: ts?.served?.bg || '#E0E7FF', border: ts?.served?.border || '#818CF8', text: ts?.served?.text || '#4F46E5', icon: '🍽️' },
                          COMPLETED: { bg: ts?.completed?.bg || '#F3F4F6', border: ts?.completed?.border || '#6B7280', text: ts?.completed?.text || '#4B5563', icon: '✅' },
                          CANCELLED: { bg: ts?.cancelled?.bg || '#FEE2E2', border: ts?.cancelled?.border || '#EF4444', text: ts?.cancelled?.text || '#DC2626', icon: '🚫' },
                        };
                        const s = styles[status] || styles.PENDING;
                        console.log('🎨 Rendering button for status:', status, 'for order:', order.id);
                        if (status === 'CANCELLED') {
                          console.log('🚫 CANCELLED button is being rendered for order:', order.id);
                          console.log('🚫 CANCELLED button styles:', s);
                        }
                        return (
                          <button
                            key={status}
                            // Bulletproof event handlers with retry mechanism - ensures clicks ALWAYS work
                            onMouseDown={(e) => {
                              console.log('🖱️ Mouse down on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onMouseUp={(e) => {
                              console.log('🖱️ Mouse up on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onClick={(e) => {
                              console.log('🖱️ Clicked on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onTouchStart={(e) => {
                              console.log('📱 Touch start on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onTouchEnd={(e) => {
                              console.log('📱 Touch end on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onPointerDown={(e) => {
                              console.log('👆 Pointer down on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onPointerUp={(e) => {
                              console.log('👆 Pointer up on status:', status, 'for order:', order.id);
                              handleClickWithRetry(order.id, status, e);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                console.log('⌨️ Keyboard activation on status:', status, 'for order:', order.id);
                                handleClickWithRetry(order.id, status, e);
                              }
                            }}
                            disabled={updatingOrders.has(order.id)}
                            className={`p-2 rounded-lg border text-left transition-all ${statusIndex === 0 ? 'col-span-2' : ''} ${updatingOrders.has(order.id) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                            style={{
                              backgroundColor: updatingOrders.has(order.id) ? '#f3f4f6' : s.bg,
                              borderColor: updatingOrders.has(order.id) ? '#d1d5db' : s.border,
                              borderWidth: statusIndex === 0 ? '2px' : '1px',
                              zIndex: 9999,
                              position: 'relative',
                              cursor: updatingOrders.has(order.id) ? 'not-allowed' : 'pointer',
                              pointerEvents: updatingOrders.has(order.id) ? 'none' : 'auto',
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none'
                            }}
                          >
                            <div className={`flex items-center gap-1.5 ${statusIndex === 0 ? 'text-sm justify-center' : 'text-xs'} font-semibold`} style={{ color: s.text }}>
                              <span className="shrink-0">{s.icon}</span>
                              <span className="whitespace-nowrap">{status.replace('_', ' ')}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click event
                openOrderModal(order);
              }}
              className="text-sm mt-2 ml-3 inline-block font-medium px-4 py-2 rounded-lg transition-all btn-size-responsive"
              style={{
                backgroundColor: 'transparent',
                color: theme.gray[600],
                border: `1px solid ${theme.gray[300]}`
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.gray[100];
                e.currentTarget.style.borderColor = theme.gray[400];
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = theme.gray[300];
              }}
            >
              Full Details
            </button>

            {/* Show Discount indicator for orders with session discount */}
            {order.hasSessionDiscount && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click event
                  setInvoiceOrderId(order.id);
                  setShowInvoiceModal(true);
                }}
                className="text-sm hover:underline mt-2 ml-3 inline-block font-medium px-responsive-sm py-responsive-xs rounded-md transition-colors btn-size-responsive"
                style={{
                  backgroundColor: 'var(--status-ok-bg)',
                  color: 'var(--status-ok-text)',
                  border: '1px solid var(--status-ok-border)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#bbf7d0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#dcfce7';
                }}
                title="View invoice with discount details"
              >
                <span className="mr-1">🏷️</span>
                Discount
              </button>
            )}

            {/* Show Invoice button for completed/ready orders, or immediately for takeaway/counter orders (not delivery) */}
            {(order.status === 'COMPLETED' || order.status === 'READY' ||
              (order.orderType && order.orderType !== 'DELIVERY' && order.orderType !== 'TABLE_SERVICE')) && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click event
                  setInvoiceOrderId(order.id);
                  setShowInvoiceModal(true);
                }}
                className="text-sm hover:underline mt-2 ml-3 inline-block font-medium px-responsive-sm py-responsive-xs rounded-md transition-colors btn-size-responsive"
                style={{
                  backgroundColor: theme.primary + '33',
                  color: theme.primary,
                  border: `1px solid ${theme.primary}`
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.primary + '55';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = theme.primary + '33';
                }}
              >
                <DocumentTextIcon className="icon-xs inline mr-1" />
                Invoice
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderOrdersContent = () => {
    // Use statistics from backend (total counts across all orders, not just current page)
    const pendingCount = statistics.PENDING;
    const acceptedCount = statistics.ACCEPTED;
    const preparingCount = statistics.PREPARING;
    const readyCount = statistics.READY;
    const servedCount = statistics.SERVED;
    const completedCount = statistics.COMPLETED;
    const cancelledCount = statistics.CANCELLED;
    const totalCount = statistics.TOTAL;

    if (loading && orders.length === 0) { // Show inline loading instead of full-page LoadingScreen
      // Don't return LoadingScreen — render page shell with inline spinner so sidebar stays visible
    }

    // Display error if it's the primary message (e.g., initial load failed)
    if (error && orders.length === 0) {
      return (
        <p
          className="p-4 border rounded-md text-center"
          style={{
            backgroundColor: theme.components?.status?.cancelled?.bg || theme.danger || '#FEE2E2',
            color: theme.components?.status?.cancelled?.text || '#B91C1C',
            borderColor: theme.components?.status?.cancelled?.border || theme.danger || '#FEE2E2',
          }}
        >
          {error}
        </p>
      );
    }

    return (
      <div ref={chromeScrollRef} className="flex flex-col h-full overflow-y-auto main-content-wrapper">
        {/* Status filter tabs — clean enterprise tab bar */}
        {(() => {
          const STATUS_TAB_COLORS: Record<string, string> = {
            ACTIVE: '#262C26', PENDING: '#C4956E', ACCEPTED: '#2D6CB5', PREPARING: '#A66A10',
            READY: '#3DA06A', SERVED: '#5D5347', COMPLETED: '#7E7261', CANCELLED: '#C55A5A',
          };
          const tabs: { key: string | null; label: string; count: number }[] = [
            { key: 'ACTIVE', label: 'Active', count: statistics.ACTIVE },
            { key: null, label: 'All', count: totalCount },
            { key: 'PENDING', label: 'Pending', count: pendingCount },
            { key: 'ACCEPTED', label: 'Accepted', count: acceptedCount },
            { key: 'PREPARING', label: 'Preparing', count: preparingCount },
            { key: 'READY', label: 'Ready', count: readyCount },
            { key: 'SERVED', label: 'Served', count: servedCount },
            { key: 'COMPLETED', label: 'Completed', count: completedCount },
            { key: 'CANCELLED', label: 'Cancelled', count: cancelledCount },
          ];
          return (
            <div className="mb-4 transition-opacity duration-200 collapsible-chrome" style={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto' }}>
              {/* Segmented status bar — same dialect as the KDS: pills share
                  the full row, active pill filled; every handler preserved. */}
              <div className="flex items-stretch gap-1.5 overflow-x-auto no-scrollbar rounded-xl p-1.5" style={{ backgroundColor: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)' }}>
                {tabs.map(tab => {
                  const isActive = tab.key === 'ACTIVE' ? statusFilter === 'ACTIVE' : tab.key === null ? statusFilter === null : statusFilter === tab.key;
                  const accent = STATUS_TAB_COLORS[tab.key || 'ACTIVE'] || '#64748B';
                  return (
                    <button
                      key={tab.key ?? 'all'}
                      onClick={() => tab.key === 'ACTIVE' ? handleStatusChipClick('ACTIVE') : tab.key === null ? handleStatusChipClick(null) : tab.key === 'COMPLETED' ? setStatusFilter(prev => (prev === 'COMPLETED' ? null : 'COMPLETED')) : tab.key === 'CANCELLED' ? setStatusFilter(prev => (prev === 'CANCELLED' ? null : 'CANCELLED')) : handleStatusChipClick(tab.key)}
                      className="relative px-3 text-[12.5px] font-semibold whitespace-nowrap transition-colors rounded-lg flex items-center justify-center gap-1.5"
                      style={{
                        flex: '1 1 0',
                        minWidth: 92,
                        minHeight: 'var(--chrome-pill-h)',
                        color: isActive ? 'var(--text-inverse)' : theme.gray?.[600] || '#555',
                        backgroundColor: isActive ? accent : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {tab.label}
                      <span
                        className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'var(--bg-surface)',
                          color: isActive ? 'var(--text-inverse)' : theme.gray?.[500] || '#777',
                          border: isActive ? 'none' : '1px solid var(--border-subtle)',
                        }}
                      >
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Notification permission banner, if needed - dismissible */}
        {!hasPermission && isNotificationSupported() && getNotificationPermission() !== 'granted' && !isNotificationBannerDismissed && (
          <div className="mb-4">
            <div
              className="p-3 border rounded-lg flex items-center justify-between gap-3"
              style={{
                backgroundColor: `${theme.primary}08`,
                color: theme.text,
                borderColor: `${theme.primary}30`,
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">🔔</span>
                <span className="text-sm">Enable notifications for real-time order updates</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors"
                  style={{ backgroundColor: theme.primary }}
                  onClick={() => requestPermission()}
                  onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                  onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  Enable
                </button>
                <button
                  className="p-1.5 rounded-full transition-all"
                  style={{
                    color: theme.gray[500],
                    backgroundColor: theme.gray[200]
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsNotificationBannerDismissed(true);
                    try {
                      localStorage.setItem('vendorNotificationBannerDismissed', 'true');
                    } catch {}
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme.gray[300];
                    e.currentTarget.style.color = theme.gray[700];
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme.gray[200];
                    e.currentTarget.style.color = theme.gray[500];
                  }}
                  title="Dismiss"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inline cooking spinner — shows during filter/fetch transitions */}
        {isFetching && sortedOrders.length > 0 && (
          <div className="flex items-center justify-center py-3">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full" style={{ backgroundColor: theme.components?.card?.background || '#fff', border: `1px solid ${theme.gray?.[200] || '#E5E5E3'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CookingSpinner theme={theme} />
              <span className="text-[11px] font-medium" style={{ color: theme.gray?.[500] || '#888' }}>Updating orders...</span>
            </div>
          </div>
        )}

        {/* Order list section — Kanban swim lanes by status */}
        {loading && orders.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full" style={{ backgroundColor: theme.components?.card?.background || '#fff', border: `1px solid ${theme.gray?.[200] || '#E5E5E3'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CookingSpinner theme={theme} />
              <span className="text-sm font-medium" style={{ color: theme.gray?.[500] || '#888' }}>Loading orders...</span>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <p className="text-center py-8" style={{ color: theme.gray[500] }}><em>No orders found.</em></p>
        ) : sortedOrders.length > 0 ? (
          (() => {
            const STATUS_ORDER = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'];
            const STATUS_COLORS: Record<string, string> = {
              PENDING: '#C4956E',
              ACCEPTED: '#2D6CB5',
              PREPARING: '#2D6CB5',
              READY: '#3DA06A',
              SERVED: '#8FA683',
              COMPLETED: '#A0A7AF',
              CANCELLED: '#C55A5A',
            };
            const grouped = new Map<string, typeof sortedOrders>();
            for (const o of sortedOrders) {
              const s = o.status || 'PENDING';
              if (!grouped.has(s)) grouped.set(s, []);
              grouped.get(s)!.push(o);
            }
            const visibleStatuses = STATUS_ORDER.filter(s => grouped.has(s));

            // Build cross-column table awareness: tableName → set of statuses it appears in
            const tableStatusMap = new Map<string, Set<string>>();
            for (const o of sortedOrders) {
              const tName = (o as any).tableName;
              if (tName) {
                if (!tableStatusMap.has(tName)) tableStatusMap.set(tName, new Set());
                tableStatusMap.get(tName)!.add(o.status || 'PENDING');
              }
            }

            // Single status selected — show full-width vertical list with table sub-grouping
            if (visibleStatuses.length === 1) {
              const singleStatusOrders = grouped.get(visibleStatuses[0])!;
              const headerColor = STATUS_COLORS[visibleStatuses[0]] || '#888';
              const singleTotal = singleStatusOrders.reduce((s: number, o: any) => s + (o.finalTotal ?? o.total ?? 0), 0);
              // Sub-group by table
              const singleTableGroups: { key: string; tableName: string | null; qrCode: string | null; orders: typeof singleStatusOrders }[] = [];
              const singleTableMap = new Map<string, typeof singleTableGroups[0]>();
              for (const o of singleStatusOrders) {
                const tName = (o as any).tableName;
                const qr = typeof (o as any).qrCode === 'string' ? (o as any).qrCode : (o as any).qrCode?.code || null;
                const key = tName ? `table-${tName}` : `solo-${o.id}`;
                if (tName && singleTableMap.has(key)) {
                  singleTableMap.get(key)!.orders.push(o);
                } else {
                  const group = { key, tableName: tName || null, qrCode: qr, orders: [o] };
                  singleTableGroups.push(group);
                  if (tName) singleTableMap.set(key, group);
                }
              }
              return (
                <div className="max-w-lg pb-4">
                  <div
                    className="flex items-center justify-between px-4 py-2.5 rounded-t-xl mb-3"
                    style={{ backgroundColor: headerColor, color: 'var(--text-inverse)' }}
                  >
                    <span className="text-sm font-bold uppercase tracking-wide">{visibleStatuses[0]}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(singleTotal)}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                        {singleStatusOrders.length}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {singleTableGroups.map(tg => {
                      const isTableGroup = tg.tableName && tg.orders.length > 1;
                      const groupKey = `${visibleStatuses[0]}-${tg.key}`;
                      const isCollapsed = collapsedTableGroups.has(groupKey);
                      const isGroupHighlighted = highlightedTable !== null && tg.tableName === highlightedTable;
                      const isGroupDimmed = highlightedTable !== null && tg.tableName !== highlightedTable;
                      const crossStatuses = tg.tableName
                        ? Array.from(tableStatusMap.get(tg.tableName) || []).filter(s => s !== visibleStatuses[0])
                        : [];
                      const STATUS_COLORS_REF: Record<string, string> = {
                        PENDING: '#D4845E', ACCEPTED: '#2D6CB5', PREPARING: '#2D6CB5',
                        READY: '#1E8E5A', SERVED: '#2F7D5B', COMPLETED: '#9CA3AF', CANCELLED: '#D2453C',
                      };
                      return (
                        <div key={tg.key} style={{ opacity: isGroupDimmed ? 0.35 : 1, transition: 'opacity 0.2s' }}>
                          {isTableGroup && (
                            <div
                              className="flex items-center gap-2 px-3 py-2.5 mb-1.5 rounded-lg cursor-pointer"
                              style={{
                                backgroundColor: isGroupHighlighted ? theme.primary + '15' : theme.primary + '08',
                                boxShadow: isGroupHighlighted ? `0 0 0 1.5px ${theme.primary}40` : 'none',
                                minHeight: 40,
                              }}
                              onClick={(e) => { e.stopPropagation(); setCollapsedTableGroups(prev => {
                                  const next = new Set(prev);
                                  next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
                                  return next;
                                });
                              }}
                            >
                              {isCollapsed
                                ? <ChevronRightIcon className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                                : <ChevronDownIcon className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                              }
                              <span
                                className="text-[13px] font-semibold"
                                style={{ color: theme.primary }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setHighlightedTable(prev => prev === tg.tableName ? null : tg.tableName);
                                }}
                              >
                                Table {tg.tableName}
                              </span>
                              <span className="text-[12px]" style={{ color: theme.gray?.[400] }}>
                                · {tg.orders.length} orders · {formatCurrency(tg.orders.reduce((s: number, o: any) => s + (o.finalTotal ?? o.total ?? 0), 0))}
                              </span>
                              {crossStatuses.length > 0 && (
                                <span className="inline-flex items-center gap-1 ml-1">
                                  {crossStatuses.map(s => (
                                    <span
                                      key={s}
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: STATUS_COLORS_REF[s] || '#999' }}
                                      title={`Also in ${s.toLowerCase()}`}
                                    />
                                  ))}
                                </span>
                              )}
                              <span className="flex-1" />
                              {tg.qrCode && (
                                <span
                                  className="text-[11px] px-2 py-1 rounded hover:opacity-80"
                                  style={{ color: theme.primary, backgroundColor: theme.primary + '15' }}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/vendor/tables?table=${tg.qrCode}`); }}
                                  title="Go to table"
                                >
                                  ↗
                                </span>
                              )}
                            </div>
                          )}
                          {!(isTableGroup && isCollapsed) && (
                            <div className={isTableGroup ? 'space-y-1.5 ml-1.5 pl-3.5 border-l-2 mb-2' : ''} style={isTableGroup ? { borderColor: theme.primary + '25' } : undefined}>
                              {tg.orders.map((order: any) => {
                                const tName = (order as any).tableName;
                                return (
                                  <CompactOrderCard
                                    key={order.id}
                                    order={order}
                                    theme={theme}
                                    getBadgeStyle={getBadgeStyle}
                                    onClick={() => openOrderModal(order as Order)}
                                    onStatusChange={handleQuickStatusUpdate}
                                    onNavigateTable={(qr) => navigate(`/vendor/tables?table=${qr}`)}
                                    onHighlightTable={(t) => setHighlightedTable(t)}
                                    isUpdating={updatingOrders.has(order.id)}
                                    actionError={orderActionErrors[order.id]}
                                    isHighlighted={highlightedTable !== null && tName === highlightedTable}
                                    isDimmed={highlightedTable !== null && tName !== highlightedTable}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // Multiple statuses — kanban swim lanes with compact order cards
            return (
              <div className="flex gap-3 overflow-x-auto pb-4 -mx-2 px-2" style={{ minHeight: 300, WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)', maskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)' }} onClick={() => { if (highlightedTable) setHighlightedTable(null); }}>
                {visibleStatuses.map(status => {
                  const statusOrders = grouped.get(status)!;
                  const headerColor = STATUS_COLORS[status] || theme.gray?.[500] || '#888';
                  const total = statusOrders.reduce((s: number, o: any) => s + (o.finalTotal ?? o.total ?? 0), 0);
                  return (
                    <div
                      key={status}
                      className="flex flex-col rounded-xl overflow-hidden"
                      style={{
                        flex: '1 1 340px',
                        minWidth: 300,
                        backgroundColor: theme.components?.card?.background || '#FFF',
                        border: `1px solid ${theme.components?.card?.border || '#E8E6E1'}`,
                      }}
                      onClick={() => { if (highlightedTable) setHighlightedTable(null); }}
                    >
                      {/* Status header — full color bar matching KDS */}
                      <div
                        className="px-4 py-2.5 flex items-center justify-between"
                        style={{ backgroundColor: headerColor, color: 'var(--text-inverse)' }}
                      >
                        <span className="text-sm font-bold uppercase tracking-wide">{status}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatCurrency(total)}</span>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                            {statusOrders.length}
                          </span>
                        </div>
                      </div>
                      {/* Compact order cards — sub-grouped by table */}
                      <div className="flex-1 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 280px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 10, alignContent: 'start' }}>
                        {(() => {
                          // Sub-group orders by table within this status column
                          const tableGroups: { key: string; tableName: string | null; qrCode: string | null; orders: typeof statusOrders }[] = [];
                          const tableMap = new Map<string, typeof tableGroups[0]>();
                          for (const o of statusOrders) {
                            const tName = (o as any).tableName;
                            const qr = typeof (o as any).qrCode === 'string' ? (o as any).qrCode : (o as any).qrCode?.code || null;
                            const key = tName ? `table-${tName}` : `solo-${o.id}`;
                            if (tName && tableMap.has(key)) {
                              tableMap.get(key)!.orders.push(o);
                            } else {
                              const group = { key, tableName: tName || null, qrCode: qr, orders: [o] };
                              tableGroups.push(group);
                              if (tName) tableMap.set(key, group);
                            }
                          }
                          return tableGroups.map(tg => {
                            const isTableGroup = tg.tableName && tg.orders.length > 1;
                            const groupKey = `${status}-${tg.key}`;
                            const isCollapsed = collapsedTableGroups.has(groupKey);
                            const isGroupHighlighted = highlightedTable !== null && tg.tableName === highlightedTable;
                            const isGroupDimmed = highlightedTable !== null && tg.tableName !== highlightedTable;
                            // Cross-column: other statuses this table has orders in
                            const crossStatuses = tg.tableName
                              ? Array.from(tableStatusMap.get(tg.tableName) || []).filter(s => s !== status)
                              : [];
                            const STATUS_COLORS_REF: Record<string, string> = {
                              PENDING: '#D4845E', ACCEPTED: '#2D6CB5', PREPARING: '#2D6CB5',
                              READY: '#1E8E5A', SERVED: '#2F7D5B', COMPLETED: '#9CA3AF', CANCELLED: '#D2453C',
                            };
                            return (
                              <div key={tg.key} style={{ opacity: isGroupDimmed ? 0.35 : 1, transition: 'opacity 0.2s' }}>
                                {/* Table sub-header — collapsible, touch-friendly */}
                                {isTableGroup && (
                                  <div
                                    className="flex items-center gap-2 px-2.5 py-2 mb-1.5 rounded-lg cursor-pointer"
                                    style={{
                                      backgroundColor: isGroupHighlighted ? theme.primary + '15' : theme.primary + '08',
                                      boxShadow: isGroupHighlighted ? `0 0 0 1.5px ${theme.primary}40` : 'none',
                                    }}
                                    onClick={(e) => { e.stopPropagation(); setCollapsedTableGroups(prev => {
                                        const next = new Set(prev);
                                        next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
                                        return next;
                                      });
                                    }}
                                  >
                                    {isCollapsed
                                      ? <ChevronRightIcon className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                                      : <ChevronDownIcon className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                                    }
                                    <span
                                      className="text-[12px] font-semibold"
                                      style={{ color: theme.primary }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHighlightedTable(prev => prev === tg.tableName ? null : tg.tableName);
                                      }}
                                    >
                                      T{tg.tableName}
                                    </span>
                                    <span className="text-[12px]" style={{ color: theme.gray?.[400] }}>
                                      · {tg.orders.length} · {formatCurrency(tg.orders.reduce((s: number, o: any) => s + (o.finalTotal ?? o.total ?? 0), 0))}
                                    </span>
                                    {/* Cross-column dots */}
                                    {crossStatuses.length > 0 && (
                                      <span className="inline-flex items-center gap-1 ml-1">
                                        {crossStatuses.map(s => (
                                          <span
                                            key={s}
                                            className="w-2 h-2 rounded-full"
                                            style={{ backgroundColor: STATUS_COLORS_REF[s] || '#999' }}
                                            title={`Also in ${s.toLowerCase()}`}
                                          />
                                        ))}
                                      </span>
                                    )}
                                    <span className="flex-1" />
                                    {/* Navigate to table */}
                                    {tg.qrCode && (
                                      <span
                                        className="text-[11px] px-2 py-1 rounded hover:opacity-80"
                                        style={{ color: theme.primary, backgroundColor: theme.primary + '15' }}
                                        onClick={(e) => { e.stopPropagation(); navigate(`/vendor/tables?table=${tg.qrCode}`); }}
                                        title="Go to table"
                                      >
                                        ↗
                                      </span>
                                    )}
                                  </div>
                                )}
                                {/* Orders — hidden when table group is collapsed */}
                                {!(isTableGroup && isCollapsed) && (
                                  <div className={isTableGroup ? 'space-y-1 ml-1 pl-2 border-l-2 mb-2' : 'space-y-2'} style={isTableGroup ? { borderColor: theme.primary + '25' } : undefined}>
                                    {tg.orders.map((order: any) => {
                                      // Per-card cross-column awareness
                                      const tName = (order as any).tableName;
                                      const cardCross = tName
                                        ? Array.from(tableStatusMap.get(tName) || []).filter(s => s !== status)
                                        : [];
                                      const isHl = highlightedTable !== null && tName === highlightedTable;
                                      const isDm = highlightedTable !== null && tName !== highlightedTable;
                                      return (
                                        <CompactOrderCard
                                          key={order.id}
                                          order={order}
                                          theme={theme}
                                          getBadgeStyle={getBadgeStyle}
                                          onClick={() => openOrderModal(order as Order)}
                                          onStatusChange={handleQuickStatusUpdate}
                                          onNavigateTable={(qr) => navigate(`/vendor/tables?table=${qr}`)}
                                          onHighlightTable={(t) => setHighlightedTable(t)}
                                          isUpdating={updatingOrders.has(order.id)}
                                          actionError={orderActionErrors[order.id]}
                                          otherStatuses={cardCross}
                                          isHighlighted={isHl}
                                          isDimmed={isDm}
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <p className="py-2" style={{ color: theme.gray[500] }}><em>No orders in this category.</em></p>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Page Info */}
            <div className="text-sm" style={{ color: theme.gray[600] }}>
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} orders
            </div>

            {/* Pagination Buttons */}
            <div className="flex items-center gap-2">
              {/* Previous Page */}
              <button
                onClick={() => {
                  if (currentPage > 0) {
                    const prevPage = currentPage - 1;
                    setCurrentPage(prevPage);
                    fetchVendorOrders(prevPage, false);
                  }
                }}
                disabled={currentPage === 0 || loading}
                className="px-2 sm:px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                style={{
                  backgroundColor: currentPage === 0 ? theme.gray[100] : theme.components.card.background,
                  color: currentPage === 0 ? theme.gray[400] : theme.text,
                  borderColor: theme.components.card.border
                }}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage < 3) {
                    pageNum = i;
                  } else if (currentPage > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => {
                        setCurrentPage(pageNum);
                        fetchVendorOrders(pageNum, false);
                      }}
                      disabled={loading}
                      className="px-3 py-1.5 text-sm rounded-md border transition-colors min-w-[36px]"
                      style={{
                        backgroundColor: pageNum === currentPage ? theme.primary : theme.components.card.background,
                        color: pageNum === currentPage ? theme.button.primary.text : theme.text,
                        borderColor: pageNum === currentPage ? theme.primary : theme.components.card.border
                      }}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              {/* Next Page */}
              <button
                onClick={() => {
                  if (currentPage < totalPages - 1) {
                    const nextPage = currentPage + 1;
                    setCurrentPage(nextPage);
                    fetchVendorOrders(nextPage, false);
                  }
                }}
                disabled={currentPage >= totalPages - 1 || loading}
                className="px-2 sm:px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                style={{
                  backgroundColor: currentPage >= totalPages - 1 ? theme.gray[100] : theme.components.card.background,
                  color: currentPage >= totalPages - 1 ? theme.gray[400] : theme.text,
                  borderColor: theme.components.card.border
                }}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Load More */}
            {currentPage < totalPages - 1 && (
              <button
                onClick={loadMoreOrders}
                disabled={isLoadingMore}
                className="px-3 py-1.5 text-[11px] font-medium rounded-md transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: 'transparent',
                  color: theme.gray?.[600] || '#555',
                  border: `1px solid ${theme.gray?.[300] || '#D1D5DB'}`,
                }}
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const pageTitle = 'Orders';

  return (
    <>
      {/* Toaster provided by ToastProvider */}
      {/* Desktop Layout: Full-width column-based */}
      <div className="vendor-orders-full-width hidden lg:block">
        <CardLayoutTheme
          title={pageTitle}
          categories={[{ id: 1, name: 'Orders' }]}
          renderItems={() => renderOrdersContent()}
          hideSidebar={true}
          compactHeader={true}
          headerActions={(
            <div className="flex items-center gap-1.5 flex-wrap transition-opacity duration-200" style={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto' }}>
              {/* Order Type filter chips */}
              {(() => {
                const types = [
                  { key: 'TABLE_SERVICE', label: 'Table' },
                  { key: 'TAKEAWAY', label: 'Takeaway' },
                  { key: 'DELIVERY', label: 'Delivery' },
                  { key: 'COUNTER_SERVICE', label: 'Counter' },
                ];
                return (
                  <div className="flex items-center gap-1 mr-1">
                    {types.map(t => {
                      const isOn = orderTypeFilters.includes(t.key);
                      return (
                        <button
                          key={t.key}
                          onClick={() => setOrderTypeFilters(prev => isOn ? prev.filter(x => x !== t.key) : [...prev, t.key])}
                          className="text-[11px] font-medium px-2 py-1 rounded-md transition-all whitespace-nowrap"
                          style={{
                            backgroundColor: isOn ? theme.text + '0D' : 'transparent',
                            color: isOn ? theme.text : theme.gray?.[400] || '#999',
                            border: `1px solid ${isOn ? theme.gray?.[400] || '#999' : theme.gray?.[200] || '#E5E5E3'}`,
                          }}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                    {orderTypeFilters.length > 0 && (
                      <button
                        onClick={() => setOrderTypeFilters([])}
                        className="text-[10px] px-1 py-0.5 rounded transition-colors"
                        style={{ color: theme.gray?.[400] || '#999', background: 'none', border: 'none', cursor: 'pointer' }}
                        title="Clear type filters"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })()}
              <div style={{ width: 1, height: 20, backgroundColor: theme.gray?.[200] || '#E5E5E3' }} />
              <DateFilterDropdown
                value={dateFilter}
                onChange={setDateFilter}
                showAllOption={true}
              />
              <SortDropdown
                value={sortBy}
                onChange={handleSortChange}
                options={sortOptions}
                sortDirection={sortDirection}
              />
              <button
                onClick={() => setIsTakeawayModalOpen(true)}
                className="rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors"
                style={{ backgroundColor: 'transparent', color: theme.gray?.[600] || '#555', border: `1px solid ${theme.gray?.[300] || '#D1D5DB'}` }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = theme.gray?.[50] || '#FAFAF8'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <ShoppingBagIcon className="w-3.5 h-3.5" />
                + Takeaway
              </button>
              {heldOrders.length > 0 && (
                <button
                  onClick={() => setShowHeldOrdersPicker(true)}
                  className="rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors"
                  style={{ backgroundColor: 'var(--status-warn-bg)', color: 'var(--status-warn-text)', border: '1px solid var(--status-warn-text)' }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#FDE68A'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#FEF3C7'; }}
                >
                  <PauseIcon className="w-3.5 h-3.5" />
                  Held ({heldOrders.length})
                </button>
              )}
            </div>
          )}
        />
      </div>

      {/* Mobile Layout */}
      <div className="vendor-orders-full-width lg:hidden">
        <CardLayoutTheme
          title={pageTitle}
          categories={[{ id: 1, name: 'Orders' }]}
          renderItems={() => renderOrdersContent()}
          hideSidebar={true}
          compactHeader={true}
          headerActions={(
            <div className="flex items-center gap-1 flex-nowrap">
              {/* Mobile: Filters (icon + text + badge) */}
              <UnifiedFilterDropdown
                orderTypeFilters={orderTypeFilters}
                dateFilter={dateFilter}
                onOrderTypeChange={setOrderTypeFilters}
                onDateFilterChange={setDateFilter}
              />
              {/* Mobile: SEARCH (icon only) */}
              <button
                onClick={() => setIsSearchModalOpen(true)}
                className="p-1.5 rounded-md transition-colors flex-shrink-0"
                style={{
                  backgroundColor: theme.accent + '33',
                  color: theme.accent,
                  border: `1px solid ${theme.accent}50`
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent + '50';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent + '33';
                }}
                title="Search Orders"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
              </button>
              {/* Mobile: TAKEAWAY (icon only with tooltip) */}
              <button
                onClick={() => setIsTakeawayModalOpen(true)}
                className="p-1.5 rounded-md transition-colors flex-shrink-0"
                style={{
                  backgroundColor: '#f59e0b20',
                  color: 'var(--status-warn-text)',
                  border: '1px solid #f59e0b50'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f59e0b40';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#f59e0b20';
                }}
                title="Takeaway Order"
              >
                <ShoppingBagIcon className="w-4 h-4" />
              </button>
              {/* Mobile: HELD ORDERS (icon + count badge) */}
              {heldOrders.length > 0 && (
                <button
                  onClick={() => setShowHeldOrdersPicker(true)}
                  className="p-1.5 rounded-md transition-colors flex-shrink-0 relative"
                  style={{
                    backgroundColor: 'var(--status-warn-bg)',
                    color: 'var(--status-warn-text)',
                    border: '1px solid var(--status-warn-text)'
                  }}
                  title={`Held Orders (${heldOrders.length})`}
                >
                  <PauseIcon className="w-4 h-4" />
                  <span
                    className="absolute -top-1 -right-1 text-[9px] font-bold rounded-full flex items-center justify-center"
                    style={{ width: 16, height: 16, backgroundColor: 'var(--status-warn-text)', color: 'var(--text-inverse)' }}
                  >
                    {heldOrders.length}
                  </span>
                </button>
              )}
              {/* Mobile: MENU (icon only with tooltip) */}
              <Link
                to={`/admin/vendors/${(user as any)?.vendorId || user?.id}/menu`}
                className="p-1.5 rounded-md transition-colors flex-shrink-0"
                style={{
                  backgroundColor: theme.accent + '33',
                  color: theme.accent,
                  border: `1px solid ${theme.accent}50`
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent + '50';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent + '33';
                }}
                title="Menu"
              >
                <Bars3Icon className="w-4 h-4" />
              </Link>
              {/* Mobile: Sort (icon only) */}
              <SortDropdown
                value={sortBy}
                onChange={handleSortChange}
                options={sortOptions}
                sortDirection={sortDirection}
                iconOnly={true}
              />
            </div>
          )}
        />
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal
        selectedOrder={selectedOrder}
        onClose={closeOrderModal}
        theme={theme}
        modalLoading={modalLoading}
        orderAnalytics={orderAnalytics}
        orderKOTs={orderKOTs}
        expandedKOTs={expandedKOTs}
        setExpandedKOTs={setExpandedKOTs}
        updatingKOTs={updatingKOTs}
        handleKOTStatusUpdate={handleKOTStatusUpdate}
        handleStatusUpdateInModal={handleStatusUpdateInModal}
        setInvoiceOrderId={setInvoiceOrderId}
        setShowInvoiceModal={setShowInvoiceModal}
        setInvoiceIncludeCompleted={setInvoiceIncludeCompleted}
      />
      {/* Invoice Modal */}
      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setInvoiceOrderId(null);
          setInvoiceIncludeCompleted(false);
        }}
        orderId={invoiceOrderId}
        includeCompleted={invoiceIncludeCompleted}
      />
      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
      {/* Takeaway Order Modal */}
      <ImprovedVendorOrderFormModal
        isOpen={isTakeawayModalOpen}
        onClose={() => { setIsTakeawayModalOpen(false); setResumeHeldOrder(null); }}
        tableId="TAKEAWAY"
        tableName="Takeaway Order"
        resumeHeldOrder={resumeHeldOrder}
        onHeldOrderChange={() => { refreshHeldOrders(); setResumeHeldOrder(null); }}
        onOrderPlaced={(newOrder) => {
          // Refresh orders list when a new takeaway order is placed
          if (newOrder) {
            setOrders(prev => [newOrder, ...prev]);
            // Refresh tab counters so Pending count updates immediately
            fetchOrderStatistics();
            toast.success('Takeaway order placed successfully!');
          }
          setIsTakeawayModalOpen(false);
          setResumeHeldOrder(null);
        }}
      />

      {/* Held Orders Picker */}
      {showHeldOrdersPicker && (
        <React.Suspense fallback={null}>
          <LazyHeldOrdersPicker
            heldOrders={heldOrders}
            onResume={(order) => {
              setShowHeldOrdersPicker(false);
              setResumeHeldOrder(order);
              setIsTakeawayModalOpen(true);
            }}
            onDelete={(id) => {
              deleteHeldOrderFromStore(id);
              refreshHeldOrders();
            }}
            onClose={() => setShowHeldOrdersPicker(false)}
          />
        </React.Suspense>
      )}

      {/* Cancellation Reason Modal */}
      {isCancelModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isCancellingOrder) {
              setIsCancelModalOpen(false);
              setCancelOrderId(null);
              setCancellationReason('');
              setCancelError(null);
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: theme.components?.card?.background || '#FFFFFF' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: theme.components?.status?.cancelled?.bg || '#FEE2E2', borderBottom: `1px solid ${theme.components?.status?.cancelled?.border || '#FECACA'}` }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: theme.components?.status?.cancelled?.border || '#FCA5A5' }}>
                <ExclamationTriangleIcon className="w-5 h-5" style={{ color: theme.components?.status?.cancelled?.text || '#DC2626' }} />
              </div>
              <div>
                <h3 className="text-base font-semibold" style={{ color: theme.components?.status?.cancelled?.text || '#991B1B' }}>
                  Cancel Order #{cancelOrderId}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: theme.components?.status?.cancelled?.text || '#B91C1C' }}>
                  This action cannot be undone
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>
                Reason for cancellation <span style={{ color: theme.components?.status?.cancelled?.text || '#DC2626' }}>*</span>
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="e.g., Customer requested cancellation, Out of stock items, etc."
                className="w-full p-3 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  color: theme.text,
                  borderColor: theme.components?.card?.border || '#D1D5DB',
                  fontSize: '16px',
                }}
                rows={3}
                maxLength={500}
                autoFocus
              />
              <div className="text-xs mt-1.5" style={{ color: theme.textSecondary || '#9CA3AF' }}>
                {cancellationReason.length}/500 characters
              </div>
            </div>

            {/* Inline error — persists next to the failing control until retry/success */}
            {cancelError && (
              <p role="alert" className="px-5 -mt-1 pb-1 text-xs font-medium" style={{ color: 'var(--status-bad)' }}>
                {cancelError}
              </p>
            )}

            {/* Actions */}
            <div className="px-5 py-4 flex gap-3" style={{ borderTop: `1px solid ${theme.components?.card?.border || '#E5E7EB'}` }}>
              <button
                type="button"
                onClick={() => {
                  setIsCancelModalOpen(false);
                  setCancelOrderId(null);
                  setCancellationReason('');
                  setCancelError(null);
                }}
                disabled={isCancellingOrder}
                className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
                style={{
                  backgroundColor: theme.components?.card?.background || '#F3F4F6',
                  color: theme.text || '#374151',
                  border: `1px solid ${theme.components?.card?.border || '#E5E7EB'}`,
                  minHeight: 44,
                }}
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleCancelOrderWithReason}
                disabled={!cancellationReason.trim() || isCancellingOrder}
                aria-busy={isCancellingOrder || undefined}
                className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                style={{
                  backgroundColor: cancellationReason.trim() && !isCancellingOrder ? (theme.components?.status?.cancelled?.text || 'var(--status-bad-text)') : (theme.components?.status?.cancelled?.border || 'var(--status-bad-border)'),
                  color: cancellationReason.trim() && !isCancellingOrder ? 'var(--text-inverse)' : (theme.components?.status?.cancelled?.bg || 'var(--status-bad-bg)'),
                  minHeight: 44,
                }}
              >
                {isCancellingOrder ? <><span className="u-btn-spinner" aria-hidden="true" />Cancelling…</> : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Popup */}
      {showPaymentConfirmation && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000]"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isConfirmingPayment) {
              setShowPaymentConfirmation(false);
              setPendingPaymentOrderId(null);
              setPaymentInfo(null);
              setPaymentError(null);
            }
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            style={{ backgroundColor: theme.card }}
          >
            {/* Header */}
            <div
              className="p-4 border-b flex items-center gap-3"
              style={{ borderColor: theme.primary + '50', backgroundColor: theme.primary + '10' }}
            >
              <div
                className="p-2 rounded-full"
                style={{ backgroundColor: theme.primary + '20' }}
              >
                <ExclamationTriangleIcon className="h-6 w-6" style={{ color: theme.primary }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
                Confirm Payment
              </h3>
            </div>

            {/* Content */}
            <div className="p-5">
              <p className="text-sm mb-4" style={{ color: theme.textSecondary || theme.text }}>
                Has the customer paid for this session?
              </p>

              <div
                className="p-4 rounded-lg mb-4"
                style={{ backgroundColor: theme.background }}
              >
                <div className="flex justify-between mb-2">
                  <span className="text-sm" style={{ color: theme.textSecondary || theme.text }}>Session Total:</span>
                  <span className="font-medium" style={{ color: theme.text }}>
                    {formatCurrency(paymentInfo?.sessionTotalAmount || 0)}
                  </span>
                </div>
                {(paymentInfo?.sessionDiscountAmount || 0) > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-sm" style={{ color: theme.success }}>🏷️ Discount Applied:</span>
                    <span className="font-medium" style={{ color: theme.success }}>
                      -{formatCurrency(paymentInfo?.sessionDiscountAmount || 0)}
                    </span>
                  </div>
                )}
                {(paymentInfo?.sessionPaidAmount || 0) > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-sm" style={{ color: theme.textSecondary || theme.text }}>Already Paid:</span>
                    <span className="font-medium" style={{ color: theme.success }}>
                      {formatCurrency(paymentInfo?.sessionPaidAmount || 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t" style={{ borderColor: theme.border }}>
                  <span className="text-sm font-semibold" style={{ color: theme.primary }}>To Collect:</span>
                  <span className="font-bold" style={{ color: theme.primary }}>
                    {formatCurrency(paymentInfo?.sessionRemainingAmount || 0)}
                  </span>
                </div>
                {(() => {
                  // When taxes are recalculated on the discounted amount, the three
                  // figures aren't pure subtraction — say so, or the gap looks wrong.
                  const total = paymentInfo?.sessionTotalAmount || 0;
                  const disc = paymentInfo?.sessionDiscountAmount || 0;
                  const paid = paymentInfo?.sessionPaidAmount || 0;
                  const remaining = paymentInfo?.sessionRemainingAmount || 0;
                  return disc > 0 && Math.abs(total - disc - paid - remaining) > 0.01 ? (
                    <p className="text-xs mt-2" style={{ color: theme.textSecondary || theme.text }}>
                      Taxes are recalculated on the discounted amount — see the invoice for the full breakdown.
                    </p>
                  ) : null;
                })()}
              </div>

              {/* Payment Method Selection */}
              <div className="mt-3 mb-2">
                <p className="text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Payment Method
                </p>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setSelectedPaymentMethod(method.value)}
                      className="px-3 py-1.5 rounded-full text-sm transition-all"
                      style={{
                        backgroundColor: selectedPaymentMethod === method.value ? theme.success : 'transparent',
                        color: selectedPaymentMethod === method.value ? 'white' : theme.text,
                        border: `1px solid ${selectedPaymentMethod === method.value ? theme.success : theme.border}`,
                        fontWeight: selectedPaymentMethod === method.value ? 600 : 400
                      }}
                    >
                      {method.icon} {method.label}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm mt-2" style={{ color: theme.textSecondary || theme.text }}>
                Review the invoice and click <strong>Paid</strong> to confirm payment received.
              </p>
            </div>

            {/* Actions */}
            <div className="p-4 border-t flex flex-col gap-3" style={{ borderColor: theme.border }}>
              {/* View Invoice and Apply Discount buttons row */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Open invoice modal for this order with all session orders (including completed)
                    if (pendingPaymentOrderId) {
                      setInvoiceOrderId(pendingPaymentOrderId);
                      setInvoiceIncludeCompleted(true); // Show full session invoice
                      setShowInvoiceModal(true);
                    }
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: theme.primary + '15',
                    color: theme.primary,
                    border: `1px solid ${theme.primary}40`
                  }}
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  View Invoice
                </button>
                {/* Apply Discount Button - Hidden for stewards */}
                {!isSteward && pendingPaymentOrderId && orders.find(o => o.id === pendingPaymentOrderId)?.orderLineId && (
                  <button
                    onClick={() => {
                      const order = orders.find(o => o.id === pendingPaymentOrderId);
                      if (order?.orderLineId) {
                        handleOpenDiscountFromPayment(order.orderLineId);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: 'var(--status-ok-bg)',
                      color: 'var(--status-ok-text)',
                      border: '1px solid var(--status-ok-border)'
                    }}
                  >
                    <span>🏷️</span>
                    {(paymentInfo?.sessionDiscountAmount || 0) > 0 ? 'Manage Discount' : 'Apply Discount'}
                  </button>
                )}
              </div>

              {/* Inline error — persists next to the failing control until retry/success */}
              {paymentError && (
                <p role="alert" className="text-xs font-medium -mt-1" style={{ color: 'var(--status-bad)' }}>
                  {paymentError}
                </p>
              )}

              {/* Action buttons row */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentConfirmation(false);
                    setPendingPaymentOrderId(null);
                    setPaymentInfo(null);
                    setSelectedPaymentMethod('');
                    setPaymentError(null);
                  }}
                  disabled={isConfirmingPayment}
                  className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: theme.background,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    minHeight: 44,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPaymentAndComplete}
                  disabled={!selectedPaymentMethod || isConfirmingPayment}
                  aria-busy={isConfirmingPayment || undefined}
                  className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: selectedPaymentMethod && !isConfirmingPayment ? theme.success : 'var(--text-disabled)',
                    minHeight: 44,
                  }}
                  title={!selectedPaymentMethod ? 'Select a payment method first' : undefined}
                >
                  {isConfirmingPayment
                    ? <><span className="u-btn-spinner" aria-hidden="true" />Settling…</>
                    : (selectedPaymentMethod ? 'Paid' : 'Select Payment Method')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Dialog */}
      {showDiscountDialog && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            style={{ backgroundColor: theme.card }}
          >
            {/* Header */}
            <div
              className="p-4 border-b flex items-center gap-3"
              style={{ borderColor: 'var(--status-ok-border)', backgroundColor: 'var(--status-ok-bg)' }}
            >
              <div className="p-2 rounded-full" style={{ backgroundColor: '#bbf7d0' }}> {/* basil-todo */}
                <span className="text-xl">🏷️</span>
              </div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--status-ok-text)' }}>
                {currentDiscount ? 'Manage Discount' : 'Apply Discount'}
              </h3>
            </div>

            {/* Content */}
            <div className="p-5">
              {currentDiscount && (
                <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--status-ok-bg)', border: '1px solid var(--status-ok-border)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--status-ok-text)' }}>
                    Current Discount: {currentDiscount.type === 'PERCENTAGE' ? `${currentDiscount.value}%` : `${CURRENCY.symbol}${currentDiscount.value}`}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--status-ok-text)' }}>
                    Amount: {CURRENCY.symbol}{currentDiscount.amount?.toFixed(2)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--status-ok-text)' }}>
                    Reason: {currentDiscount.reason}
                  </p>
                </div>
              )}

              {/* An offer that fits this bill. Shown before the manual fields
                  because it is the right answer most of the time. */}
              {billOffers?.available && !currentDiscount && (
                <div
                  data-testid="bill-offer-available"
                  className="mb-4 p-3 rounded-lg flex items-center gap-3 flex-wrap"
                  style={{ backgroundColor: 'var(--bg-selected)', border: '1px solid var(--action)' }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold" style={{ color: 'var(--action)' }}>
                      {billOffers.names.join(' + ')}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {CURRENCY.symbol}{billOffers.discountAmount.toFixed(2)} off this bill
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="apply-bill-offer"
                    disabled={applyingOffer}
                    onClick={handleApplyOffer}
                    className="px-3 py-1.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: 'var(--action)' }}
                  >
                    {applyingOffer ? 'Applying…' : 'Apply offer'}
                  </button>
                </div>
              )}

              {/* Discount Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Discount Type
                </label>
                <div className="flex gap-2">
                  {(['PERCENTAGE', 'FIXED', 'COUPON'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setDiscountType(type)}
                      className="flex-1 px-3 py-2 rounded-lg text-sm transition-all"
                      style={{
                        backgroundColor: discountType === type ? '#166534' : 'transparent',
                        color: discountType === type ? 'white' : theme.text,
                        border: `1px solid ${discountType === type ? '#166534' : theme.border}`,
                        fontWeight: discountType === type ? 600 : 400
                      }}
                    >
                      {type === 'PERCENTAGE' ? '%' : type === 'FIXED' ? CURRENCY.symbol : '🎫'} {type.charAt(0) + type.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Coupon Code (if coupon type) */}
              {discountType === 'COUPON' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                    Coupon Code *
                  </label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.background,
                      color: theme.text,
                      border: `1px solid ${theme.border}`
                    }}
                  />
                </div>
              )}

              {/* Discount Value */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  {discountType === 'PERCENTAGE' ? 'Percentage (%)' : `Amount (${CURRENCY.symbol})`} *
                </label>
                <input
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === 'PERCENTAGE' ? 'e.g., 10' : 'e.g., 50'}
                  max={discountType === 'PERCENTAGE' ? 100 : undefined}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.background,
                    color: theme.text,
                    border: `1px solid ${theme.border}`
                  }}
                />
              </div>

              {/* Reason */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Reason * <span className="text-xs font-normal" style={{ color: theme.textSecondary }}>(Mandatory)</span>
                </label>
                <input
                  type="text"
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder="e.g., Customer complaint, Loyalty discount"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.background,
                    color: theme.text,
                    border: `1px solid ${discountReason.trim() === '' && discountValue !== '' ? '#ef4444' : theme.border}`
                  }}
                />
                {discountReason.trim() === '' && discountValue !== '' && (
                  <p className="text-xs mt-1" style={{ color: 'var(--status-bad)' }}>
                    Reason is mandatory — the discount cannot be applied without it.
                  </p>
                )}
              </div>
            </div>

            {/* Inline error — persists next to the failing control until retry/success */}
            {discountError && (
              <p role="alert" className="px-4 pb-2 text-xs font-medium" style={{ color: 'var(--status-bad)' }}>
                {discountError}
              </p>
            )}

            {/* Actions */}
            <div className="p-4 border-t flex gap-3" style={{ borderColor: theme.border }}>
              <button
                type="button"
                onClick={() => { setShowDiscountDialog(false); setDiscountError(null); }}
                disabled={isApplyingDiscount}
                className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
                style={{
                  backgroundColor: theme.background,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  minHeight: 44,
                }}
              >
                Cancel
              </button>
              {currentDiscount && (
                <button
                  type="button"
                  onClick={handleRemoveDiscount}
                  disabled={isApplyingDiscount}
                  aria-busy={isApplyingDiscount || undefined}
                  className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  style={{ backgroundColor: theme.danger || 'var(--status-bad)', minHeight: 44 }}
                >
                  {isApplyingDiscount ? <><span className="u-btn-spinner" aria-hidden="true" />Removing…</> : 'Remove'}
                </button>
              )}
              <button
                type="button"
                onClick={handleApplyDiscount}
                disabled={isApplyingDiscount}
                aria-busy={isApplyingDiscount || undefined}
                className="u-focus-ring flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--status-ok-text)', minHeight: 44 }}
              >
                {isApplyingDiscount ? <><span className="u-btn-spinner" aria-hidden="true" />Applying…</> : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorOrders;
